import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import "./OnlineBookingSectionCompact.css";
import { getCurrentSalonId, getCurrentSalonName } from "../utils/currentSalon";
import {
  getPortalSettings,
  getPortalHelperMapping,
  autoMapPortalHelperMapping,
  savePortalHelperMapping,
  uploadPortalLogo,
  deletePortalLogo,
  uploadPortalBackground,
  deletePortalBackground,
  updatePortalSettings,
} from "../api/clientPortalAdmin";
import { listCategories, listServices } from "../api/services";
import SBINModal from "../components/modals/SBINModal";
import TimeSelect from "../components/TimeSelect";
import ImageUploadField from "../components/ImageUploadField";
import InfoTooltip from "../components/common/InfoTooltip";
import ModalCloseX from "../components/common/ModalCloseX";
import { formatNANP } from "../utils/phone";
import {
  buildMapEmbedUrl,
  buildMapLinkUrl,
  formatAddressText,
} from "../utils/portalContact";
import {
  portalEmailDefaults,
  portalEmailFieldHelp,
  portalEmailTooltips,
} from "../constants/clientPortalEmailHelp";
import SafeHtml from "../components/common/SafeHtml";
import {
  DEFAULT_PORTAL_PRIMARY_COLOR,
  DEFAULT_PORTAL_TEXT_COLOR,
  hasLowContrast,
  normalizeHexColor,
  resolvePortalBrandColors,
} from "../utils/portalBranding";


const fieldHelp = {
  enabled:
    "Turn this on to make the client portal public for this salon.",
  portal_ai_enabled:
    "Turn on the booking helper in the client portal booking flow.",
  portal_ai_autoselect_enabled:
    "Allow the helper to auto-select a service when clients tap \"Select this service\".",
  portal_ai_tone: "Choose the helper tone clients will see.",
  default_ai_pricing_response_mode:
    "Choose how the booking helper talks about pricing by default for this salon.",
  default_ai_pricing_disclaimer:
    "Shown when a service uses starting-at pricing and needs a salon-wide disclaimer.",
  hidden_price_assistant_message:
    "Shown when the helper should avoid revealing a price and guide the client toward booking.",
  enable_ai_price_estimates:
    "Turn this off to stop the booking helper from sharing any prices in client conversations.",
  allow_service_level_pricing_override:
    "Allow individual services to override the salon-wide pricing behavior.",
  brand_name: "Shown as the portal brand name.",
  hero_title: "Main headline on the portal landing page.",
  hero_subtitle: "Short supporting text under the headline.",
  logo_url: "Upload a logo to show on the portal.",
  background_url: "Upload a background image for the landing page.",
  primary_color: "Accent color used for buttons and highlights.",
  portal_nav_color: "Header color used across the public booking portal.",
  portal_button_text_color:
    "Ellyanna default is white. Change only if your button color needs different text.",
  portal_nav_text_color:
    "Ellyanna default is white. Change only if your header color needs different text.",
  client_cancel_modify_cutoff_hours:
    "Clients can cancel or reschedule up to this many hours before the appointment start time.",
};

const ColorControl = ({
  label,
  value,
  onChange,
  helper,
  fallbackColor = DEFAULT_PORTAL_PRIMARY_COLOR,
  resetValue,
  warning,
}) => {
  const resolvedColor = normalizeHexColor(value) || fallbackColor;
  const resolvedResetValue = resetValue ?? fallbackColor;
  return (
    <div className="space-y-2">
      <label className="text-sm md:text-base font-semibold tracking-wide text-slate-500">
        {label}
      </label>
      <div className="flex flex-wrap items-center gap-2">
        <input
          type="color"
          value={resolvedColor}
          onChange={(event) => onChange(event.target.value.toUpperCase())}
          className="h-11 w-14 rounded-lg border border-slate-300 bg-white p-1"
          aria-label={label}
        />
        <input
          type="text"
          value={value}
          onChange={(event) => onChange(event.target.value)}
          placeholder={fallbackColor}
          className="h-11 md:h-12 min-w-[180px] flex-1 rounded-xl border border-slate-300 px-4"
        />
        <button
          type="button"
          onClick={() => onChange(resolvedResetValue)}
          className="rounded-full border border-slate-300 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
        >
          Reset
        </button>
      </div>
      {helper && <p className="text-xs text-slate-500">{helper}</p>}
      {warning && <p className="text-xs font-semibold text-amber-700">{warning}</p>}
    </div>
  );
};

const LANE_GROUPS = [
  {
    label: "Nails",
    options: [
      { value: "NAILS", label: "Nails (All nail services)" },
      { value: "NAILS_NATURAL_HANDS", label: "Natural hands" },
      { value: "NAILS_ARTIFICIAL_HANDS", label: "Artificial hands" },
      { value: "NAILS_FEET", label: "Feet" },
      { value: "NAILS_ADD_ONS", label: "Nail add-ons" },
    ],
  },
  {
    label: "Waxing",
    options: [
      { value: "WAXING", label: "Waxing (All waxing)" },
      { value: "WAX_FACE", label: "Face" },
      { value: "WAX_BODY", label: "Body" },
      { value: "WAX_BIKINI_BRAZILIAN", label: "Bikini & Brazilian" },
    ],
  },
  {
    label: "Lashes",
    options: [
      { value: "LASHES", label: "Lashes (All lash services)" },
      { value: "LASH_EXTENSIONS", label: "Extensions" },
      { value: "LASH_LIFT_TINT", label: "Lift & Tint" },
    ],
  },
  {
    label: "Facials & Esthetics",
    options: [
      { value: "FACIALS_ESTHETICS", label: "Facials & Esthetics (All)" },
      { value: "FACIAL_BASIC", label: "Basic facial" },
      { value: "FACIAL_ADVANCED", label: "Advanced facial" },
      { value: "ESTHETICS_BODY", label: "Body treatments" },
    ],
  },
  {
    label: "PMU",
    options: [
      { value: "PMU", label: "PMU (All PMU services)" },
      { value: "PMU_BROWS_MICROBLADING", label: "Microblading/Brows" },
      { value: "PMU_TOUCHUP", label: "Touch-ups" },
    ],
  },
  {
    label: "Other",
    options: [
      { value: "OTHER", label: "Other" },
      { value: "UNKNOWN", label: "Unknown" },
    ],
  },
];

const LANE_LABELS = LANE_GROUPS.flatMap((group) => group.options).reduce(
  (acc, option) => {
    if (!acc[option.value]) {
      acc[option.value] = option.label;
    }
    return acc;
  },
  {
    NAILS: "Nails",
    WAXING: "Waxing",
    LASHES: "Lashes",
    FACIALS_ESTHETICS: "Facials & Esthetics",
    PMU: "PMU",
    OTHER: "Other",
    UNKNOWN: "Unknown",
  }
);

const formatSuggestedLane = (item) => {
  if (!item) return "";
  const suggested = item.suggested_lane;
  const parent = item.suggested_parent_lane;
  const suggestedLabel = LANE_LABELS[suggested] || suggested || "";
  const parentLabel = LANE_LABELS[parent] || parent || "";
  if (parent && suggested && parent !== suggested) {
    return `${parentLabel} -> ${suggestedLabel}`;
  }
  if (parentLabel) return parentLabel;
  return suggestedLabel;
};

const cleanText = (value) => {
  if (value == null) return null;
  const trimmed = String(value).trim();
  return trimmed ? trimmed : null;
};

const escapeHtml = (value) =>
  String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const replaceTokens = (template, values) =>
  String(template || "").replace(
    /{{\s*([a-zA-Z0-9_]+)\s*}}/g,
    (match, key) => (values?.[key] ? String(values[key]) : "")
  );

const REGISTRATION_TOKENS = [
  "{{client_first_name}}",
  "{{salon_name}}",
  "{{portal_login_url}}",
  "{{set_password_url}}",
  "{{salon_phone}}",
  "{{signature_name}}",
];

const PASSWORD_RESET_TOKENS = [
  "{{client_first_name}}",
  "{{salon_name}}",
  "{{portal_login_url}}",
  "{{reset_password_url}}",
  "{{salon_phone}}",
  "{{signature_name}}",
];

const PORTAL_HOUR_LABELS = [
  "Mon",
  "Tue",
  "Wed",
  "Thu",
  "Fri",
  "Sat",
  "Sun",
];
const PORTAL_HOUR_KEYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];

const coerceBool = (value) => {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value === 1;
  if (typeof value === "string") {
    return value.trim().toLowerCase() === "true";
  }
  return false;
};

const normalizeTime = (value) => {
  if (value == null) return "";
  const str = String(value).trim();
  if (!str) return "";
  const parts = str.split(":");
  const hours = Number(parts[0]);
  if (Number.isNaN(hours)) return "";
  let minutesPart = parts[1] || "00";
  const digits = minutesPart.match(/^(\d+)/);
  let minutes = digits ? Number(digits[1]) : 0;
  if (Number.isNaN(minutes)) {
    minutes = 0;
  }
  minutes = Math.max(0, Math.min(59, minutes));
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
};

const normalizePortalHoursMap = (value) => {
  const map = value && typeof value === "object" ? value : {};
  return PORTAL_HOUR_KEYS.map((key, idx) => {
    const entry = map?.[key] || {};
    const isClosed = coerceBool(entry.closed ?? entry.is_closed);
    return {
      key,
      day_of_week: idx,
      label: PORTAL_HOUR_LABELS[idx],
      is_closed: isClosed,
      open_time: isClosed
        ? ""
        : normalizeTime(entry.open ?? entry.open_time ?? "09:00"),
      close_time: isClosed
        ? ""
        : normalizeTime(entry.close ?? entry.close_time ?? "18:00"),
    };
  });
};

const portalHoursRowsToMap = (rows = []) => {
  const normalized = {};
  rows.forEach((row) => {
    const key = PORTAL_HOUR_KEYS[row.day_of_week];
    if (!key) return;
    if (row.is_closed) {
      normalized[key] = { closed: true };
    } else {
      normalized[key] = {
        closed: false,
        open: normalizeTime(row.open_time),
        close: normalizeTime(row.close_time),
      };
    }
  });
  return normalized;
};

const formatRemapTimestamp = (value) => {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return parsed.toLocaleString();
};

const extractAutoMapSuggestions = (data) => {
  const suggestedCategoryMap =
    data?.suggested_category_lane_map || data?.category_lane_map || {};
  const suggestedOverrides =
    data?.suggested_service_lane_overrides ||
    data?.service_lane_overrides ||
    {};
  const conflicts = Array.isArray(data?.conflicts) ? data.conflicts : [];
  const allServicesReview = Array.isArray(data?.all_services_review)
    ? data.all_services_review
    : [];
  const servicesNeedingReview = Array.isArray(data?.services_needing_review)
    ? data.services_needing_review
    : [];
  return {
    suggestedCategoryMap,
    suggestedOverrides,
    conflicts,
    allServicesReview,
    servicesNeedingReview,
    summary: data?.summary,
    meta: data?.meta,
  };
};

export default function ClientPortalAIAssistant() {
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const storedToken = token;
  const salonId = getCurrentSalonId(user);
  const salonName = getCurrentSalonName(user);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [cutoffError, setCutoffError] = useState("");
  const [helperLoading, setHelperLoading] = useState(false);
  const [helperSaving, setHelperSaving] = useState(false);
  const [helperAutoMapping, setHelperAutoMapping] = useState(false);
  const [helperError, setHelperError] = useState("");
  const [helperSuccess, setHelperSuccess] = useState("");
  const [helperMapping, setHelperMapping] = useState(null);
  const [categoryLaneMapDraft, setCategoryLaneMapDraft] = useState({});
  const [serviceLaneOverridesDraft, setServiceLaneOverridesDraft] = useState({});
  const [servicesNeedingReview, setServicesNeedingReview] = useState([]);
  const [helperConflicts, setHelperConflicts] = useState([]);
  const [reviewMode, setReviewMode] = useState("CONFLICTS");
  const [allReviewSearch, setAllReviewSearch] = useState("");
  const [allReviewLane, setAllReviewLane] = useState("");
  const [allReviewCategory, setAllReviewCategory] = useState("");
  const [helperCategories, setHelperCategories] = useState([]);
  const [helperServices, setHelperServices] = useState([]);
  const [helperExpanded, setHelperExpanded] = useState(true);
  const [helperGuideOpen, setHelperGuideOpen] = useState(false);

  const [showAdvancedAssistantSettings, setShowAdvancedAssistantSettings] =
    useState(false);

  const [showCategoryMappingSettings, setShowCategoryMappingSettings] =
    useState(false);
  const [helperRemapOpen, setHelperRemapOpen] = useState(false);
  const [helperRemapMode, setHelperRemapMode] = useState("replace");
  const [helperRemapping, setHelperRemapping] = useState(false);
  const [helperLastRemapAt, setHelperLastRemapAt] = useState(null);
  const [helperToastMessage, setHelperToastMessage] = useState("");
  const [helperToastVariant, setHelperToastVariant] = useState("success");
  const [guideOpen, setGuideOpen] = useState(false);
  const showFullSettings = false;
  const helperToastTimerRef = useRef(null);
  const helperSetupRef = useRef(null);
  const [activeEmailField, setActiveEmailField] = useState(null);
  const registrationSubjectRef = useRef(null);
  const registrationBodyRef = useRef(null);
  const resetSubjectRef = useRef(null);
  const resetBodyRef = useRef(null);
  const [form, setForm] = useState({
    enabled: false,
    portal_ai_enabled: false,
    portal_ai_autoselect_enabled: true,
    portal_ai_tone: "friendly",
    default_ai_pricing_response_mode: "EXACT_PRICE",
    default_ai_pricing_disclaimer: "",
    hidden_price_assistant_message: "",
    enable_ai_price_estimates: true,
    allow_service_level_pricing_override: true,
    brand_name: "",
    hero_title: "",
    hero_subtitle: "",
    logo_url: "",
    background_url: "",
    primary_color: DEFAULT_PORTAL_PRIMARY_COLOR,
    portal_nav_color: DEFAULT_PORTAL_PRIMARY_COLOR,
    portal_button_text_color: DEFAULT_PORTAL_TEXT_COLOR,
    portal_nav_text_color: DEFAULT_PORTAL_TEXT_COLOR,
    review_email_enabled: true,
    review_send_mode: "on_close",
    review_send_delay_minutes: 0,
    review_quiet_hours_enabled: true,
    review_quiet_start_local: "20:00",
    review_quiet_end_local: "09:00",
    review_reminder_enabled: false,
    review_reminder_delay_days: 3,
    review_reminder_max_count: 1,
    review_kpi_min_reviews: 10,
    review_kpi_window_days: 90,
    review_kpi_weight_overall: 60,
    review_kpi_weight_service: 25,
    review_kpi_weight_staff: 15,
    review_followup_enabled: true,
    review_followup_threshold: 3,
    review_followup_default_assignee_user_id: "",
    google_review_url: "",
    facebook_url: "",
    instagram_url: "",
    tiktok_url: "",
    youtube_url: "",
    review_notify_email: "",
    registration_email_template_enabled: false,
    registration_email_subject: portalEmailDefaults.registration.subject,
    registration_email_body: portalEmailDefaults.registration.body,
    password_reset_email_template_enabled: false,
    password_reset_email_subject: portalEmailDefaults.password_reset.subject,
    password_reset_email_body: portalEmailDefaults.password_reset.body,
    client_cancel_modify_cutoff_hours: 24,
  });
  const [portalContactDefaults, setPortalContactDefaults] = useState(null);
  const [portalContactOverrides, setPortalContactOverrides] = useState({
    phone: null,
    address_line1: null,
    address_line2: null,
    city: null,
    province: null,
    postal_code: null,
    country: null,
  });
  const [portalHoursDraft, setPortalHoursDraft] = useState(() =>
    normalizePortalHoursMap(null)
  );
  const [portalHoursIsOverride, setPortalHoursIsOverride] = useState(false);

  const loadSettings = async () => {
    if (!salonId) return;
    setLoading(true);
    setError("");
    try {
      const data = await getPortalSettings(salonId, storedToken);
      const settingsData = data?.portal_settings || data || {};
      const contactData = data?.portal_contact_location || {};
      const defaults = contactData?.defaults || null;
      const overrides = contactData?.overrides || null;
        setForm({
          enabled: Boolean(settingsData?.enabled),
          portal_ai_enabled: Boolean(settingsData?.portal_ai_enabled),
          portal_ai_autoselect_enabled:
            settingsData?.portal_ai_autoselect_enabled !== false,
          portal_ai_tone: settingsData?.portal_ai_tone || "friendly",
          default_ai_pricing_response_mode:
            settingsData?.default_ai_pricing_response_mode || "EXACT_PRICE",
          default_ai_pricing_disclaimer:
            settingsData?.default_ai_pricing_disclaimer || "",
          hidden_price_assistant_message:
            settingsData?.hidden_price_assistant_message || "",
          enable_ai_price_estimates:
            settingsData?.enable_ai_price_estimates !== false,
          allow_service_level_pricing_override:
            settingsData?.allow_service_level_pricing_override !== false,
          brand_name: settingsData?.brand_name || "",
          hero_title: settingsData?.hero_title || "",
          hero_subtitle: settingsData?.hero_subtitle || "",
          logo_url: settingsData?.logo_url || "",
          background_url: settingsData?.background_url || "",
          primary_color: settingsData?.primary_color || DEFAULT_PORTAL_PRIMARY_COLOR,
          portal_nav_color:
            settingsData?.portal_nav_color || DEFAULT_PORTAL_PRIMARY_COLOR,
          portal_button_text_color:
            settingsData?.portal_button_text_color || DEFAULT_PORTAL_TEXT_COLOR,
          portal_nav_text_color:
            settingsData?.portal_nav_text_color || DEFAULT_PORTAL_TEXT_COLOR,
          review_email_enabled: settingsData?.review_email_enabled !== false,
          review_send_mode: settingsData?.review_send_mode || "on_close",
          review_send_delay_minutes: Number.isFinite(
            settingsData?.review_send_delay_minutes
          )
            ? Number(settingsData.review_send_delay_minutes)
            : 0,
          review_quiet_hours_enabled:
            settingsData?.review_quiet_hours_enabled !== false,
          review_quiet_start_local: normalizeTime(
            settingsData?.review_quiet_start_local || "20:00"
          ),
          review_quiet_end_local: normalizeTime(
            settingsData?.review_quiet_end_local || "09:00"
          ),
          review_reminder_enabled: Boolean(settingsData?.review_reminder_enabled),
          review_reminder_delay_days: Number.isFinite(
            settingsData?.review_reminder_delay_days
          )
            ? Number(settingsData.review_reminder_delay_days)
            : 3,
          review_reminder_max_count: Number.isFinite(
            settingsData?.review_reminder_max_count
          )
            ? Number(settingsData.review_reminder_max_count)
            : 1,
          review_kpi_min_reviews: Number.isFinite(settingsData?.review_kpi_min_reviews)
            ? Number(settingsData.review_kpi_min_reviews)
            : 10,
          review_kpi_window_days: Number.isFinite(settingsData?.review_kpi_window_days)
            ? Number(settingsData.review_kpi_window_days)
            : 90,
          review_kpi_weight_overall: Number.isFinite(
            settingsData?.review_kpi_weight_overall
          )
            ? Number(settingsData.review_kpi_weight_overall)
            : 60,
          review_kpi_weight_service: Number.isFinite(
            settingsData?.review_kpi_weight_service
          )
            ? Number(settingsData.review_kpi_weight_service)
            : 25,
          review_kpi_weight_staff: Number.isFinite(
            settingsData?.review_kpi_weight_staff
          )
            ? Number(settingsData.review_kpi_weight_staff)
            : 15,
          review_followup_enabled:
            settingsData?.review_followup_enabled !== false,
          review_followup_threshold: Number.isFinite(
            settingsData?.review_followup_threshold
          )
            ? Number(settingsData.review_followup_threshold)
            : 3,
          review_followup_default_assignee_user_id:
            settingsData?.review_followup_default_assignee_user_id || "",
          google_review_url: settingsData?.google_review_url || "",
          facebook_url: settingsData?.facebook_url || "",
          instagram_url: settingsData?.instagram_url || "",
          tiktok_url: settingsData?.tiktok_url || "",
          youtube_url: settingsData?.youtube_url || "",
          review_notify_email: settingsData?.review_notify_email || "",
          registration_email_template_enabled: Boolean(
            settingsData?.registration_email_template_enabled
          ),
          registration_email_subject:
            settingsData?.registration_email_subject ||
            portalEmailDefaults.registration.subject,
          registration_email_body:
            settingsData?.registration_email_body ||
            portalEmailDefaults.registration.body,
          password_reset_email_template_enabled: Boolean(
            settingsData?.password_reset_email_template_enabled
          ),
          password_reset_email_subject:
            settingsData?.password_reset_email_subject ||
            portalEmailDefaults.password_reset.subject,
          password_reset_email_body:
            settingsData?.password_reset_email_body ||
            portalEmailDefaults.password_reset.body,
          client_cancel_modify_cutoff_hours:
            Number.isFinite(settingsData?.client_cancel_modify_cutoff_hours)
              ? Number(settingsData.client_cancel_modify_cutoff_hours)
            : 24,
      });
      setPortalContactDefaults(defaults);
      setPortalContactOverrides({
        phone: overrides?.phone ?? null,
        address_line1: overrides?.address?.line1 ?? null,
        address_line2: overrides?.address?.line2 ?? null,
        city: overrides?.address?.city ?? null,
        province: overrides?.address?.province ?? null,
        postal_code: overrides?.address?.postal_code ?? null,
        country: overrides?.address?.country ?? null,
      });
      const overrideHours = overrides?.hours || null;
      const sourceHours = overrideHours || defaults?.hours || null;
      setPortalHoursDraft(normalizePortalHoursMap(sourceHours));
      setPortalHoursIsOverride(Boolean(overrideHours));
    } catch (err) {
      setError(
        err?.response?.data?.detail || "Unable to load portal settings."
      );
    } finally {
      setLoading(false);
    }
  };

  const loadHelperSetup = async () => {
    if (!salonId) return;
    setHelperLoading(true);
    setHelperError("");
    try {
      const [mappingData, categoriesData, servicesData] = await Promise.all([
        getPortalHelperMapping(salonId, storedToken),
        listCategories({ kind: "service", include_inactive: true }, storedToken),
        listServices({ salon_id: salonId }, storedToken),
      ]);
      const categories = Array.isArray(categoriesData)
        ? categoriesData.filter((category) => category?.is_active !== false)
        : [];
      const services = Array.isArray(servicesData)
        ? servicesData.filter(
            (service) =>
              service?.is_active !== false &&
              service?.show_in_online_booking !== false &&
              service?.is_product !== true
          )
        : [];
      setHelperMapping(mappingData || null);
      setCategoryLaneMapDraft(mappingData?.category_lane_map || {});
      setServiceLaneOverridesDraft(mappingData?.service_lane_overrides || {});
      setServicesNeedingReview(
        Array.isArray(mappingData?.services_needing_review)
          ? mappingData.services_needing_review
          : []
      );
      setHelperConflicts([]);
      setReviewMode("CONFLICTS");
      setAllReviewSearch("");
      setAllReviewLane("");
      setAllReviewCategory("");
      setHelperCategories(categories);
      setHelperServices(services);
    } catch (err) {
      setHelperError(
        err?.response?.data?.detail || "Unable to load helper setup."
      );
    } finally {
      setHelperLoading(false);
    }
  };

  useEffect(() => {
    if (!storedToken || !salonId) return;
    loadSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storedToken, salonId]);

  useEffect(() => {
    if (!storedToken || !salonId) return;
    if (!form.portal_ai_enabled) {
      setHelperMapping(null);
      setCategoryLaneMapDraft({});
      setServiceLaneOverridesDraft({});
      setServicesNeedingReview([]);
      setHelperConflicts([]);
      setReviewMode("CONFLICTS");
      setAllReviewSearch("");
      setAllReviewLane("");
      setAllReviewCategory("");
      setHelperCategories([]);
      setHelperServices([]);
      setHelperLastRemapAt(null);
      setHelperError("");
      setHelperSuccess("");
      return;
    }
    loadHelperSetup();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storedToken, salonId, form.portal_ai_enabled]);

  useEffect(() => {
    if (!form.portal_ai_enabled) return;
    setHelperGuideOpen(false);
  }, [form.portal_ai_enabled, helperMapping?.is_confirmed]);

  useEffect(() => {
    return () => {
      if (helperToastTimerRef.current) {
        clearTimeout(helperToastTimerRef.current);
      }
    };
  }, []);

  const renderEmailLabelWithTooltip = (labelText, tooltipKey) => (
    <span className="inline-flex items-center gap-2">
      <span>{labelText}</span>
      <InfoTooltip text={portalEmailTooltips[tooltipKey]} label={labelText} />
    </span>
  );

  const portalPreviewOrigin = useMemo(() => {
    if (typeof window === "undefined") return "https://example.com";
    return window.location?.origin || "https://example.com";
  }, []);

  const sampleEmailValues = useMemo(() => {
    const salonPhone =
      portalContactOverrides?.phone ||
      portalContactDefaults?.phone ||
      "(555) 555-5555";
    const loginUrl = salonId
      ? `${portalPreviewOrigin}/portal/salon/${salonId}/login`
      : `${portalPreviewOrigin}/portal/login`;
    const setupUrl = salonId
      ? `${portalPreviewOrigin}/portal/setup?token=sample&salonId=${salonId}`
      : `${portalPreviewOrigin}/portal/setup?token=sample`;
    const resetUrl = salonId
      ? `${portalPreviewOrigin}/portal/reset-password?token=sample&salonId=${salonId}`
      : `${portalPreviewOrigin}/portal/reset-password?token=sample`;
    return {
      client_first_name: "Liliane",
      salon_name: salonName || "Your Salon",
      portal_login_url: loginUrl,
      set_password_url: setupUrl,
      reset_password_url: resetUrl,
      salon_phone: salonPhone,
      signature_name: salonName || "Your Salon",
    };
  }, [portalContactDefaults, portalContactOverrides, portalPreviewOrigin, salonId, salonName]);

  const emailFieldRefs = {
    registration_subject: registrationSubjectRef,
    registration_body: registrationBodyRef,
    reset_subject: resetSubjectRef,
    reset_body: resetBodyRef,
  };

  const handleEmailTokenInsert = (tokenValue, section) => {
    if (!tokenValue) return;
    const fieldKey = activeEmailField;
    const targetRef = fieldKey ? emailFieldRefs[fieldKey] : null;
    const target = targetRef?.current;
    const isRegistration = fieldKey?.startsWith("registration");
    const isReset = fieldKey?.startsWith("reset");
    if (
      (section === "registration" && !isRegistration) ||
      (section === "reset" && !isReset)
    ) {
      if (typeof navigator !== "undefined" && navigator.clipboard) {
        navigator.clipboard.writeText(tokenValue).catch(() => {});
      }
      return;
    }
    const isEnabled = isRegistration
      ? form.registration_email_template_enabled
      : isReset
        ? form.password_reset_email_template_enabled
        : false;
    if (isEnabled && target && typeof target.selectionStart === "number") {
      const start = target.selectionStart;
      const end = target.selectionEnd ?? start;
      if (fieldKey === "registration_subject") {
        setForm((prev) => ({
          ...prev,
          registration_email_subject:
            prev.registration_email_subject.slice(0, start) +
            tokenValue +
            prev.registration_email_subject.slice(end),
        }));
      } else if (fieldKey === "registration_body") {
        setForm((prev) => ({
          ...prev,
          registration_email_body:
            prev.registration_email_body.slice(0, start) +
            tokenValue +
            prev.registration_email_body.slice(end),
        }));
      } else if (fieldKey === "reset_subject") {
        setForm((prev) => ({
          ...prev,
          password_reset_email_subject:
            prev.password_reset_email_subject.slice(0, start) +
            tokenValue +
            prev.password_reset_email_subject.slice(end),
        }));
      } else if (fieldKey === "reset_body") {
        setForm((prev) => ({
          ...prev,
          password_reset_email_body:
            prev.password_reset_email_body.slice(0, start) +
            tokenValue +
            prev.password_reset_email_body.slice(end),
        }));
      }
      requestAnimationFrame(() => {
        target.focus();
        const nextPos = start + tokenValue.length;
        target.setSelectionRange(nextPos, nextPos);
      });
    }
    if (typeof navigator !== "undefined" && navigator.clipboard) {
      navigator.clipboard.writeText(tokenValue).catch(() => {});
    }
  };

  const registrationSubjectTemplate = form.registration_email_template_enabled
    ? form.registration_email_subject || portalEmailDefaults.registration.subject
    : portalEmailDefaults.registration.subject;
  const registrationBodyTemplate = form.registration_email_template_enabled
    ? form.registration_email_body || portalEmailDefaults.registration.body
    : portalEmailDefaults.registration.body;

  const passwordSubjectTemplate = form.password_reset_email_template_enabled
    ? form.password_reset_email_subject || portalEmailDefaults.password_reset.subject
    : portalEmailDefaults.password_reset.subject;
  const passwordBodyTemplate = form.password_reset_email_template_enabled
    ? form.password_reset_email_body || portalEmailDefaults.password_reset.body
    : portalEmailDefaults.password_reset.body;

  const registrationPreviewSubject = replaceTokens(
    registrationSubjectTemplate,
    sampleEmailValues
  );
  const registrationPreviewBodyHtml = useMemo(() => {
    const replaced = replaceTokens(registrationBodyTemplate, sampleEmailValues);
    return escapeHtml(replaced).replace(/\n/g, "<br />");
  }, [registrationBodyTemplate, sampleEmailValues]);

  const passwordPreviewSubject = replaceTokens(
    passwordSubjectTemplate,
    sampleEmailValues
  );
  const passwordPreviewBodyHtml = useMemo(() => {
    const replaced = replaceTokens(passwordBodyTemplate, sampleEmailValues);
    return escapeHtml(replaced).replace(/\n/g, "<br />");
  }, [passwordBodyTemplate, sampleEmailValues]);

  const brandPreviewColors = useMemo(
    () =>
      resolvePortalBrandColors({
        primary_color: form.primary_color,
        portal_nav_color: form.portal_nav_color,
        portal_button_text_color: form.portal_button_text_color,
        portal_nav_text_color: form.portal_nav_text_color,
      }),
    [
      form.primary_color,
      form.portal_nav_color,
      form.portal_button_text_color,
      form.portal_nav_text_color,
    ]
  );
  const isDefaultButtonColorPair =
    normalizeHexColor(form.portal_button_text_color) ===
      DEFAULT_PORTAL_TEXT_COLOR &&
    brandPreviewColors.primary === DEFAULT_PORTAL_PRIMARY_COLOR;
  const isDefaultNavColorPair =
    normalizeHexColor(form.portal_nav_text_color) === DEFAULT_PORTAL_TEXT_COLOR &&
    brandPreviewColors.nav === DEFAULT_PORTAL_PRIMARY_COLOR;
  const buttonContrastWarning =
    form.portal_button_text_color &&
    !isDefaultButtonColorPair &&
    hasLowContrast(form.portal_button_text_color, brandPreviewColors.primary)
      ? "This button text color may be hard to read."
      : "";
  const navContrastWarning =
    form.portal_nav_text_color &&
    !isDefaultNavColorPair &&
    hasLowContrast(form.portal_nav_text_color, brandPreviewColors.nav)
      ? "This header text color may be hard to read."
      : "";

  const showHelperToast = (message, variant = "success") => {
    if (helperToastTimerRef.current) {
      clearTimeout(helperToastTimerRef.current);
    }
    setHelperToastVariant(variant);
    setHelperToastMessage(message);
    helperToastTimerRef.current = setTimeout(() => {
      setHelperToastMessage("");
    }, 3200);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!salonId) return;
    setSaving(true);
    setError("");
    setSuccess("");
    setCutoffError("");
    const cutoffValue = Number(form.client_cancel_modify_cutoff_hours);
    if (
      !Number.isInteger(cutoffValue) ||
      cutoffValue < 0 ||
      cutoffValue > 168
    ) {
      setCutoffError("Cutoff must be an integer between 0 and 168.");
      setSaving(false);
      return;
    }
    if (form.registration_email_template_enabled) {
      const subjectText = String(form.registration_email_subject || "").trim();
      const bodyText = String(form.registration_email_body || "").trim();
      if (!subjectText || !bodyText) {
        setError(
          "Please enter a subject and message, or turn off custom message."
        );
        setSaving(false);
        return;
      }
    }
    if (form.password_reset_email_template_enabled) {
      const subjectText = String(form.password_reset_email_subject || "").trim();
      const bodyText = String(form.password_reset_email_body || "").trim();
      if (!subjectText || !bodyText) {
        setError(
          "Please enter a subject and message, or turn off custom message."
        );
        setSaving(false);
        return;
      }
    }
    try {
      const payload = {
        enabled: Boolean(form.enabled),
        portal_ai_enabled: Boolean(form.portal_ai_enabled),
        portal_ai_autoselect_enabled: Boolean(form.portal_ai_autoselect_enabled),
        portal_ai_tone: cleanText(form.portal_ai_tone) || "friendly",
        default_ai_pricing_response_mode:
          cleanText(form.default_ai_pricing_response_mode) || "EXACT_PRICE",
        default_ai_pricing_disclaimer: cleanText(form.default_ai_pricing_disclaimer),
        hidden_price_assistant_message: cleanText(form.hidden_price_assistant_message),
        enable_ai_price_estimates: Boolean(form.enable_ai_price_estimates),
        allow_service_level_pricing_override: Boolean(
          form.allow_service_level_pricing_override
        ),
        brand_name: cleanText(form.brand_name),
        hero_title: cleanText(form.hero_title),
        hero_subtitle: cleanText(form.hero_subtitle),
        logo_url: cleanText(form.logo_url),
        background_url: cleanText(form.background_url),
        primary_color: cleanText(form.primary_color),
        portal_nav_color: cleanText(form.portal_nav_color),
        portal_button_text_color: cleanText(form.portal_button_text_color),
        portal_nav_text_color: cleanText(form.portal_nav_text_color),
        review_email_enabled: Boolean(form.review_email_enabled),
        review_send_mode: cleanText(form.review_send_mode) || "on_close",
        review_send_delay_minutes: Number(form.review_send_delay_minutes || 0),
        review_quiet_hours_enabled: Boolean(form.review_quiet_hours_enabled),
        review_quiet_start_local: cleanText(form.review_quiet_start_local),
        review_quiet_end_local: cleanText(form.review_quiet_end_local),
        review_reminder_enabled: Boolean(form.review_reminder_enabled),
        review_reminder_delay_days: Number(form.review_reminder_delay_days || 0),
        review_reminder_max_count: Number(form.review_reminder_max_count || 0),
        review_kpi_min_reviews: Number(form.review_kpi_min_reviews || 0),
        review_kpi_window_days: Number(form.review_kpi_window_days || 0),
        review_kpi_weight_overall: Number(form.review_kpi_weight_overall || 0),
        review_kpi_weight_service: Number(form.review_kpi_weight_service || 0),
        review_kpi_weight_staff: Number(form.review_kpi_weight_staff || 0),
        review_followup_enabled: Boolean(form.review_followup_enabled),
        review_followup_threshold: Number(form.review_followup_threshold || 0),
          review_followup_default_assignee_user_id:
            form.review_followup_default_assignee_user_id || null,
          google_review_url: cleanText(form.google_review_url),
          facebook_url: cleanText(form.facebook_url),
          instagram_url: cleanText(form.instagram_url),
          tiktok_url: cleanText(form.tiktok_url),
          youtube_url: cleanText(form.youtube_url),
          review_notify_email: cleanText(form.review_notify_email),
        registration_email_template_enabled: Boolean(
          form.registration_email_template_enabled
        ),
        registration_email_subject: cleanText(form.registration_email_subject),
        registration_email_body: cleanText(form.registration_email_body),
        password_reset_email_template_enabled: Boolean(
          form.password_reset_email_template_enabled
        ),
        password_reset_email_subject: cleanText(form.password_reset_email_subject),
        password_reset_email_body: cleanText(form.password_reset_email_body),
        client_cancel_modify_cutoff_hours: cutoffValue,
        portal_phone: portalContactOverrides.phone ?? null,
        portal_address_line1: portalContactOverrides.address_line1 ?? null,
        portal_address_line2: portalContactOverrides.address_line2 ?? null,
        portal_city: portalContactOverrides.city ?? null,
        portal_province: portalContactOverrides.province ?? null,
        portal_postal_code: portalContactOverrides.postal_code ?? null,
        portal_country: portalContactOverrides.country ?? null,
        portal_hours_json: portalHoursIsOverride
          ? portalHoursRowsToMap(portalHoursDraft)
          : null,
      };
      const updated = await updatePortalSettings(salonId, payload, storedToken);
      const settingsData = updated?.portal_settings || updated || {};
      const contactData = updated?.portal_contact_location || {};
      const defaults = contactData?.defaults || null;
      const overrides = contactData?.overrides || null;
      setForm({
        enabled: Boolean(settingsData?.enabled),
        portal_ai_enabled: Boolean(settingsData?.portal_ai_enabled),
        portal_ai_autoselect_enabled:
          settingsData?.portal_ai_autoselect_enabled !== false,
        portal_ai_tone: settingsData?.portal_ai_tone || "friendly",
        default_ai_pricing_response_mode:
          settingsData?.default_ai_pricing_response_mode || "EXACT_PRICE",
        default_ai_pricing_disclaimer:
          settingsData?.default_ai_pricing_disclaimer || "",
        hidden_price_assistant_message:
          settingsData?.hidden_price_assistant_message || "",
        enable_ai_price_estimates:
          settingsData?.enable_ai_price_estimates !== false,
        allow_service_level_pricing_override:
          settingsData?.allow_service_level_pricing_override !== false,
        brand_name: settingsData?.brand_name || "",
        hero_title: settingsData?.hero_title || "",
        hero_subtitle: settingsData?.hero_subtitle || "",
        logo_url: settingsData?.logo_url || "",
        background_url: settingsData?.background_url || "",
        primary_color: settingsData?.primary_color || DEFAULT_PORTAL_PRIMARY_COLOR,
        portal_nav_color:
          settingsData?.portal_nav_color || DEFAULT_PORTAL_PRIMARY_COLOR,
        portal_button_text_color:
          settingsData?.portal_button_text_color || DEFAULT_PORTAL_TEXT_COLOR,
        portal_nav_text_color:
          settingsData?.portal_nav_text_color || DEFAULT_PORTAL_TEXT_COLOR,
        review_email_enabled: settingsData?.review_email_enabled !== false,
        review_send_mode: settingsData?.review_send_mode || "on_close",
        review_send_delay_minutes: Number.isFinite(
          settingsData?.review_send_delay_minutes
        )
          ? Number(settingsData.review_send_delay_minutes)
          : 0,
        review_quiet_hours_enabled:
          settingsData?.review_quiet_hours_enabled !== false,
        review_quiet_start_local: normalizeTime(
          settingsData?.review_quiet_start_local || "20:00"
        ),
        review_quiet_end_local: normalizeTime(
          settingsData?.review_quiet_end_local || "09:00"
        ),
        review_reminder_enabled: Boolean(settingsData?.review_reminder_enabled),
        review_reminder_delay_days: Number.isFinite(
          settingsData?.review_reminder_delay_days
        )
          ? Number(settingsData.review_reminder_delay_days)
          : 3,
        review_reminder_max_count: Number.isFinite(
          settingsData?.review_reminder_max_count
        )
          ? Number(settingsData.review_reminder_max_count)
          : 1,
        review_kpi_min_reviews: Number.isFinite(settingsData?.review_kpi_min_reviews)
          ? Number(settingsData.review_kpi_min_reviews)
          : 10,
        review_kpi_window_days: Number.isFinite(settingsData?.review_kpi_window_days)
          ? Number(settingsData.review_kpi_window_days)
          : 90,
        review_kpi_weight_overall: Number.isFinite(
          settingsData?.review_kpi_weight_overall
        )
          ? Number(settingsData.review_kpi_weight_overall)
          : 60,
        review_kpi_weight_service: Number.isFinite(
          settingsData?.review_kpi_weight_service
        )
          ? Number(settingsData.review_kpi_weight_service)
          : 25,
        review_kpi_weight_staff: Number.isFinite(
          settingsData?.review_kpi_weight_staff
        )
          ? Number(settingsData.review_kpi_weight_staff)
          : 15,
        review_followup_enabled: settingsData?.review_followup_enabled !== false,
        review_followup_threshold: Number.isFinite(
          settingsData?.review_followup_threshold
        )
          ? Number(settingsData.review_followup_threshold)
          : 3,
        review_followup_default_assignee_user_id:
          settingsData?.review_followup_default_assignee_user_id || "",
        google_review_url: settingsData?.google_review_url || "",
        facebook_url: settingsData?.facebook_url || "",
        instagram_url: settingsData?.instagram_url || "",
        tiktok_url: settingsData?.tiktok_url || "",
        youtube_url: settingsData?.youtube_url || "",
        review_notify_email: settingsData?.review_notify_email || "",
        registration_email_template_enabled: Boolean(
          settingsData?.registration_email_template_enabled
        ),
        registration_email_subject:
          settingsData?.registration_email_subject ||
          portalEmailDefaults.registration.subject,
        registration_email_body:
          settingsData?.registration_email_body ||
          portalEmailDefaults.registration.body,
        password_reset_email_template_enabled: Boolean(
          settingsData?.password_reset_email_template_enabled
        ),
        password_reset_email_subject:
          settingsData?.password_reset_email_subject ||
          portalEmailDefaults.password_reset.subject,
        password_reset_email_body:
          settingsData?.password_reset_email_body ||
          portalEmailDefaults.password_reset.body,
        client_cancel_modify_cutoff_hours:
          Number.isFinite(settingsData?.client_cancel_modify_cutoff_hours)
            ? Number(settingsData.client_cancel_modify_cutoff_hours)
            : 24,
      });
      setPortalContactDefaults(defaults);
      setPortalContactOverrides({
        phone: overrides?.phone ?? null,
        address_line1: overrides?.address?.line1 ?? null,
        address_line2: overrides?.address?.line2 ?? null,
        city: overrides?.address?.city ?? null,
        province: overrides?.address?.province ?? null,
        postal_code: overrides?.address?.postal_code ?? null,
        country: overrides?.address?.country ?? null,
      });
      const overrideHours = overrides?.hours || null;
      const sourceHours = overrideHours || defaults?.hours || null;
      setPortalHoursDraft(normalizePortalHoursMap(sourceHours));
      setPortalHoursIsOverride(Boolean(overrideHours));
      setSuccess("Portal settings saved.");
    } catch (err) {
      setError(
        err?.response?.data?.detail || "Unable to save portal settings."
      );
    } finally {
      setSaving(false);
    }
  };

  const serviceCounts = useMemo(() => {
    const counts = {};
    helperServices.forEach((service) => {
      if (!service?.category_id) return;
      const key = String(service.category_id);
      counts[key] = (counts[key] || 0) + 1;
    });
    return counts;
  }, [helperServices]);

  const serviceDurationById = useMemo(() => {
    const durations = {};
    helperServices.forEach((service) => {
      if (service?.id == null) return;
      durations[String(service.id)] = Number(service.duration_minutes || 0);
    });
    return durations;
  }, [helperServices]);

  const reviewCategoryOptions = useMemo(() => {
    const options = helperCategories.map((category) => ({
      value: String(category.id),
      label: category.name,
    }));
    options.sort((a, b) => a.label.localeCompare(b.label));
    return options;
  }, [helperCategories]);

  const filteredAllReviewItems = useMemo(() => {
    if (reviewMode !== "ALL") return [];
    let items = servicesNeedingReview || [];
    const searchValue = allReviewSearch.trim().toLowerCase();
    if (searchValue) {
      items = items.filter((item) =>
        String(item.service_name || "").toLowerCase().includes(searchValue)
      );
    }
    if (allReviewLane) {
      items = items.filter(
        (item) =>
          item.suggested_lane === allReviewLane ||
          item.suggested_parent_lane === allReviewLane
      );
    }
    if (allReviewCategory) {
      items = items.filter(
        (item) => String(item.category_id || "") === allReviewCategory
      );
    }
    return items;
  }, [
    reviewMode,
    servicesNeedingReview,
    allReviewSearch,
    allReviewLane,
    allReviewCategory,
  ]);

  const helperReviewItems = useMemo(() => {
    if (reviewMode === "ALL") {
      return filteredAllReviewItems.map((item) => ({
        ...item,
        duration_minutes:
          item.duration_minutes ||
          serviceDurationById[String(item.service_id)] ||
          0,
      }));
    }
    if (servicesNeedingReview.length > 0) {
      return servicesNeedingReview.map((item) => ({
        ...item,
        duration_minutes:
          item.duration_minutes ||
          serviceDurationById[String(item.service_id)] ||
          0,
      }));
    }
    return helperConflicts.map((conflict) => ({
      ...conflict,
      duration_minutes:
        conflict.duration_minutes ||
        serviceDurationById[String(conflict.service_id)] ||
        0,
    }));
  }, [
    reviewMode,
    filteredAllReviewItems,
    servicesNeedingReview,
    helperConflicts,
    serviceDurationById,
  ]);
  const helperConfirmed = helperMapping?.is_confirmed === true;
  const helperCategoryCount = Object.keys(categoryLaneMapDraft || {}).length;
  const helperOverrideCount = Object.keys(
    serviceLaneOverridesDraft || {}
  ).length;
  const hasReviewItems =
    reviewMode === "ALL"
      ? servicesNeedingReview.length > 0
      : helperReviewItems.length > 0;


  const visibleHelperReviewItems = helperReviewItems.slice(0, 20);


  const hiddenHelperReviewCount = Math.max(
    0,
    helperReviewItems.length - visibleHelperReviewItems.length
  );


  const helperServicesTotal =
    Number(helperMapping?.summary?.services_total) || helperServices.length;


  const helperCategoriesTotal =
    Number(helperMapping?.summary?.categories_total) || helperCategories.length;


  const helperMappedCategoriesCount = helperCategories.filter((category) => {
    const lane = String(
      categoryLaneMapDraft?.[String(category.id)] || ""
    ).toUpperCase();
    return lane && lane !== "UNKNOWN";
  }).length;


  const helperReviewedServicesCount = Math.max(
    0,
    helperServicesTotal - servicesNeedingReview.length
  );


  const helperNeedsReviewCount = servicesNeedingReview.length;
  const hasHelperMapping =
    helperCategoryCount > 0 || helperOverrideCount > 0 || helperConfirmed;

  const helperReadyToConfirm = hasHelperMapping && helperNeedsReviewCount === 0;
  const showAutoMap = !hasHelperMapping;
  const showRemap = hasHelperMapping;
  const showHelperBanner =
    form.portal_ai_enabled && helperMapping?.is_confirmed !== true;
  const portalDefaultAddress = portalContactDefaults?.address || {};
  const portalDefaultPhone = portalContactDefaults?.phone || "";
  const resolvePortalValue = (overrideValue, defaultValue) => {
    const overrideText = cleanText(overrideValue);
    if (overrideText) return overrideText;
    const defaultText = cleanText(defaultValue);
    return defaultText || "";
  };
  const portalEffectivePhone = useMemo(
    () => resolvePortalValue(portalContactOverrides?.phone, portalDefaultPhone),
    [portalContactOverrides?.phone, portalDefaultPhone]
  );
  const portalEffectiveAddress = useMemo(
    () => ({
      line1: resolvePortalValue(
        portalContactOverrides?.address_line1,
        portalDefaultAddress?.line1
      ),
      line2: resolvePortalValue(
        portalContactOverrides?.address_line2,
        portalDefaultAddress?.line2
      ),
      city: resolvePortalValue(
        portalContactOverrides?.city,
        portalDefaultAddress?.city
      ),
      province: resolvePortalValue(
        portalContactOverrides?.province,
        portalDefaultAddress?.province
      ),
      postal_code: resolvePortalValue(
        portalContactOverrides?.postal_code,
        portalDefaultAddress?.postal_code
      ),
      country: resolvePortalValue(
        portalContactOverrides?.country,
        portalDefaultAddress?.country
      ),
    }),
    [portalContactOverrides, portalDefaultAddress]
  );
  const hasAddressOverride = useMemo(
    () =>
      Boolean(
        portalContactOverrides?.address_line1 ||
          portalContactOverrides?.address_line2 ||
          portalContactOverrides?.city ||
          portalContactOverrides?.province ||
          portalContactOverrides?.postal_code ||
          portalContactOverrides?.country
      ),
    [portalContactOverrides]
  );
  const effectiveAddressText = useMemo(
    () => formatAddressText(portalEffectiveAddress),
    [portalEffectiveAddress]
  );
  const mapEmbedUrl = useMemo(
    () => buildMapEmbedUrl(effectiveAddressText),
    [effectiveAddressText]
  );
  const mapLinkUrl = useMemo(
    () => buildMapLinkUrl(effectiveAddressText),
    [effectiveAddressText]
  );

  const handleCategoryLaneChange = (categoryId, lane) => {
    const key = String(categoryId);
    setCategoryLaneMapDraft((prev) => ({
      ...prev,
      [key]: lane,
    }));
  };

  const handleServiceOverrideChange = (serviceId, lane) => {
    const key = String(serviceId);
    setServiceLaneOverridesDraft((prev) => {
      const overrides = { ...(prev || {}) };
      if (!lane) {
        delete overrides[key];
      } else {
        overrides[key] = lane;
      }
      return overrides;
    });
  };

  const handlePortalContactChange = (field, value, defaultValue) => {
    const trimmed = String(value || "").trim();
    const defaultTrimmed = String(defaultValue || "").trim();
    setPortalContactOverrides((prev) => {
      const next = { ...prev };
      if (!trimmed || (defaultTrimmed && trimmed === defaultTrimmed)) {
        next[field] = null;
      } else {
        next[field] = trimmed;
      }
      return next;
    });
  };

  const resetPortalPhone = () => {
    setPortalContactOverrides((prev) => ({ ...prev, phone: null }));
  };

  const resetPortalAddress = () => {
    setPortalContactOverrides((prev) => ({
      ...prev,
      address_line1: null,
      address_line2: null,
      city: null,
      province: null,
      postal_code: null,
      country: null,
    }));
  };

  const handlePortalLogoUpload = async (file) => {
    if (!salonId) {
      throw new Error("Salon is required to upload a logo.");
    }
    const data = await uploadPortalLogo(salonId, file, storedToken);
    return data?.url || "";
  };

  const handlePortalLogoRemove = async () => {
    if (!salonId) {
      throw new Error("Salon is required to remove a logo.");
    }
    const data = await deletePortalLogo(salonId, storedToken);
    return data?.url || "";
  };

  const handlePortalBackgroundUpload = async (file) => {
    if (!salonId) {
      throw new Error("Salon is required to upload a background.");
    }
    const data = await uploadPortalBackground(salonId, file, storedToken);
    return data?.url || "";
  };

  const handlePortalBackgroundRemove = async () => {
    if (!salonId) {
      throw new Error("Salon is required to remove a background.");
    }
    const data = await deletePortalBackground(salonId, storedToken);
    return data?.url || "";
  };

  const updatePortalHourField = (dayOfWeek, field, value) => {
    setPortalHoursDraft((prev) =>
      prev.map((row) =>
        row.day_of_week === dayOfWeek ? { ...row, [field]: value } : row
      )
    );
    setPortalHoursIsOverride(true);
  };

  const handleCopyPortalHours = () => {
    const source = portalContactDefaults?.hours || null;
    setPortalHoursDraft(normalizePortalHoursMap(source));
    setPortalHoursIsOverride(true);
  };

  const handleResetPortalHours = () => {
    const source = portalContactDefaults?.hours || null;
    setPortalHoursDraft(normalizePortalHoursMap(source));
    setPortalHoursIsOverride(false);
  };

  const handleReviewSetup = () => {
    setHelperExpanded(true);
    if (helperMapping?.is_confirmed !== true) {
      setHelperGuideOpen(true);
    }
    requestAnimationFrame(() => {
      helperSetupRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  const handleAutoMap = async () => {
    if (!salonId) return;
    setHelperAutoMapping(true);
    setHelperError("");
    setHelperSuccess("");
    try {
      const data = await autoMapPortalHelperMapping(salonId, storedToken);
      const {
        suggestedCategoryMap,
        suggestedOverrides,
        servicesNeedingReview,
        conflicts,
        summary,
        meta,
      } = extractAutoMapSuggestions(data);
      setCategoryLaneMapDraft({ ...suggestedCategoryMap });
      setServiceLaneOverridesDraft({ ...suggestedOverrides });
      setServicesNeedingReview(servicesNeedingReview);
      setHelperConflicts(conflicts);
      setReviewMode("CONFLICTS");
      setAllReviewSearch("");
      setAllReviewLane("");
      setAllReviewCategory("");
      setHelperMapping((prev) => ({
        ...(prev || {}),
        summary: summary || prev?.summary,
        is_confirmed: false,
      }));
      setHelperLastRemapAt(meta?.generated_at || new Date().toISOString());
      setHelperExpanded(true);
      setHelperSuccess("Auto-map complete. Review and save your setup.");
    } catch (err) {
      setHelperError(
        err?.response?.data?.detail || "Unable to auto-map helper setup."
      );
    } finally {
      setHelperAutoMapping(false);
    }
  };

  const openRemapModal = () => {
    setHelperRemapMode(helperConfirmed ? "fill" : "replace");
    setHelperRemapOpen(true);
  };

  const handleRemap = async () => {
    if (!salonId) return;
    setHelperRemapping(true);
    setHelperError("");
    setHelperSuccess("");
    try {
      const data = await autoMapPortalHelperMapping(salonId, storedToken);
      const {
        suggestedCategoryMap,
        suggestedOverrides,
        servicesNeedingReview,
        conflicts,
        allServicesReview,
        summary,
        meta,
      } = extractAutoMapSuggestions(data);
      if (helperRemapMode === "replace") {
        setCategoryLaneMapDraft({ ...suggestedCategoryMap });
        setServiceLaneOverridesDraft({});
        setServicesNeedingReview(allServicesReview);
        setReviewMode("ALL");
        setAllReviewSearch("");
        setAllReviewLane("");
        setAllReviewCategory("");
      } else if (helperRemapMode === "categories") {
        setCategoryLaneMapDraft({ ...suggestedCategoryMap });
        setServiceLaneOverridesDraft((prev) => ({ ...prev }));
        setServicesNeedingReview(servicesNeedingReview);
        setReviewMode("CONFLICTS");
      } else {
        setCategoryLaneMapDraft((prev) => {
          const next = { ...(prev || {}) };
          Object.entries(suggestedCategoryMap || {}).forEach(([key, lane]) => {
            const currentLane = String(next[key] || "").toUpperCase();
            if (!currentLane || currentLane === "UNKNOWN") {
              next[key] = lane;
            }
          });
          return next;
        });
        setServiceLaneOverridesDraft((prev) => {
          const next = { ...(prev || {}) };
          Object.entries(suggestedOverrides || {}).forEach(([key, lane]) => {
            if (!next[key]) {
              next[key] = lane;
            }
          });
          return next;
        });
        setServicesNeedingReview(servicesNeedingReview);
        setReviewMode("CONFLICTS");
      }
      setHelperConflicts(conflicts);
      setHelperMapping((prev) => ({
        ...(prev || {}),
        summary: summary || prev?.summary,
        is_confirmed: false,
      }));
      setHelperLastRemapAt(meta?.generated_at || new Date().toISOString());
      setHelperExpanded(true);
      const hasSuggestions =
        Object.keys(suggestedCategoryMap || {}).length > 0 ||
        Object.keys(suggestedOverrides || {}).length > 0;
      const hasConflicts = Array.isArray(conflicts) && conflicts.length > 0;
      if (
        helperRemapMode !== "replace" &&
        !hasSuggestions &&
        !hasConflicts
      ) {
        showHelperToast(
          "No changes found. Your mapping already looks up to date.",
          "info"
        );
      } else {
        setHelperSuccess("Auto-map suggestions applied. Review and save.");
        showHelperToast(
          "Auto-map suggestions applied. Review and save.",
          "success"
        );
      }
      setHelperRemapOpen(false);
    } catch (err) {
      if (process.env.NODE_ENV !== "production") {
        console.error("Helper remap failed", err);
      }
      setHelperError(
        err?.response?.data?.detail || "Auto-map failed. Please try again."
      );
      showHelperToast("Auto-map failed. Please try again.", "error");
    } finally {
      setHelperRemapping(false);
    }
  };

  const handleSaveHelper = async (confirm) => {
    if (!salonId) return;
    setHelperSaving(true);
    setHelperError("");
    setHelperSuccess("");
    try {
      const payload = {
        salon_id: salonId,
        category_lane_map: categoryLaneMapDraft || {},
        service_lane_overrides: serviceLaneOverridesDraft || {},
        is_confirmed: Boolean(confirm),
      };
      const data = await savePortalHelperMapping(payload, storedToken);
      setHelperMapping(data || null);
      setCategoryLaneMapDraft(data?.category_lane_map || {});
      setServiceLaneOverridesDraft(data?.service_lane_overrides || {});
      if (confirm || reviewMode !== "ALL") {
        setServicesNeedingReview(
          Array.isArray(data?.services_needing_review)
            ? data.services_needing_review
            : []
        );
      }
      setHelperConflicts([]);
      if (confirm) {
        setReviewMode("CONFLICTS");
        setAllReviewSearch("");
        setAllReviewLane("");
        setAllReviewCategory("");
      }
      setHelperSuccess(
        confirm ? "Service matching marked ready." : "Service matching saved."
      );
    } catch (err) {
      setHelperError(
        err?.response?.data?.detail || "Unable to save service matching."
      );
    } finally {
      setHelperSaving(false);
    }
  };

  const handleClose = (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    if (typeof window !== "undefined" && window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate("/manager/schedule");
  };


  const applySuggestedMappingsToReviewItems = () => {
    const reviewItems = helperReviewItems || [];
    if (reviewItems.length === 0) return;

    let appliedCount = 0;
    setServiceLaneOverridesDraft((prev) => {
      const overrides = { ...(prev || {}) };
      reviewItems.forEach((item) => {
        const suggested =
          item.suggested_lane && item.suggested_lane !== "UNKNOWN"
            ? item.suggested_lane
            : item.suggested_parent_lane;
        if (!suggested || suggested === "UNKNOWN") return;
        overrides[String(item.service_id)] = suggested;
        appliedCount += 1;
      });
      return overrides;
    });

    if (appliedCount > 0) {
      setHelperSuccess(
        `${appliedCount} suggested service ${
          appliedCount === 1 ? "match" : "matches"
        } applied. Save when ready.`
      );
      showHelperToast("Suggested matches applied. Save when ready.", "success");
    }
  };

  const renderSaveSettingsButton = (extraClassName = "") => (
    <button
      type="submit"
      disabled={saving}
      className={`h-10 md:h-11 rounded-full bg-slate-900 px-5 text-sm md:text-base font-semibold text-white hover:bg-slate-800 disabled:opacity-60 ${extraClassName}`}
    >
      {saving ? "Saving..." : "Save Settings"}
    </button>
  );

  return (
    <div className="onlineBookingShell">
      <div className="onlineBookingWide online-booking-wide online-booking-compact text-base md:text-lg">
        <div className="rounded-3xl border border-slate-200 bg-white shadow-2xl overflow-hidden">
          <div className="bg-rose-500 px-6 py-4 relative">
            <h1 className="text-xl md:text-2xl font-semibold text-white">
              Client Booking Helper - AI Assistant
            </h1>
            <p className="text-sm md:text-base text-rose-100">
              Control how the helper guides clients to the right services and prevents
              wrong bookings.
            </p>
            <div
              className="absolute right-4 top-3 z-50"
              onClick={handleClose}
              onMouseDown={(event) => {
                event.preventDefault();
                event.stopPropagation();
              }}
            >
              <ModalCloseX onClick={handleClose} />
            </div>
          </div>

          <div className="px-6 py-6 space-y-6">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="text-sm text-slate-500">
                  {salonName || "Salon"}
                  {salonId ? ` - Salon ID ${salonId}` : ""}
                </div>
              </div>
              {salonId && (
                <a
                  href={`/portal/salon/${salonId}`}
                  target="_blank"
                  rel="noreferrer"
                  className="rounded-full border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                >
                  Preview Portal
                </a>
              )}
            </div>

            {error && (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-rose-700 text-sm md:text-base">
                {error}
              </div>
            )}
            {success && (
              <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-emerald-700 text-sm md:text-base">
                {success}
              </div>
            )}
            {showHelperBanner && (
              <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-amber-900 text-sm md:text-base">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="font-semibold">
                    Finish helper setup so clients book the right service.
                  </div>
                  <button
                    type="button"
                    onClick={handleReviewSetup}
                    className="rounded-full border border-amber-300 px-4 py-2 text-xs font-semibold text-amber-900 hover:bg-amber-100"
                  >
                    Review setup
                  </button>
                </div>
              </div>
            )}

            {loading ? (
              <div className="text-slate-500">Loading settings...</div>
            ) : (
              <form onSubmit={handleSave} className="space-y-6">
                {showFullSettings && (
                  <>
                    <div className="rounded-2xl border border-slate-200 bg-white p-6 space-y-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h2 className="text-lg md:text-xl font-semibold text-slate-900">
                        Portal Settings
                      </h2>
                      <p className="text-sm text-slate-500">
                        Control branding and visibility for your salon.
                      </p>
                    </div>
                  </div>

                  <label className="flex items-center gap-3 text-sm md:text-base text-slate-700">
                    <input
                      type="checkbox"
                      checked={!!form.enabled}
                      onChange={(e) =>
                        setForm((prev) => ({ ...prev, enabled: e.target.checked }))
                      }
                      className="h-4 w-4 rounded border-slate-300 text-slate-900"
                    />
                    <span className="font-semibold text-slate-700">Enabled</span>
                  </label>
                  <p className="text-xs text-slate-500">{fieldHelp.enabled}</p>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="text-sm md:text-base font-semibold tracking-wide text-slate-500">
                        Brand Name
                      </label>
                      <input
                        type="text"
                        value={form.brand_name}
                        onChange={(e) =>
                          setForm((prev) => ({
                            ...prev,
                            brand_name: e.target.value,
                          }))
                        }
                        className="h-11 md:h-12 w-full rounded-xl border border-slate-300 px-4"
                      />
                      <p className="text-xs text-slate-500">{fieldHelp.brand_name}</p>
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm md:text-base font-semibold tracking-wide text-slate-500">
                        Hero Title
                      </label>
                      <input
                        type="text"
                        value={form.hero_title}
                        onChange={(e) =>
                          setForm((prev) => ({
                            ...prev,
                            hero_title: e.target.value,
                          }))
                        }
                        className="h-11 md:h-12 w-full rounded-xl border border-slate-300 px-4"
                      />
                      <p className="text-xs text-slate-500">{fieldHelp.hero_title}</p>
                    </div>
                    <div className="space-y-2 lg:col-span-2">
                      <label className="text-sm md:text-base font-semibold tracking-wide text-slate-500">
                        Hero Subtitle
                      </label>
                      <input
                        type="text"
                        value={form.hero_subtitle}
                        onChange={(e) =>
                          setForm((prev) => ({
                            ...prev,
                            hero_subtitle: e.target.value,
                          }))
                        }
                        className="h-11 md:h-12 w-full rounded-xl border border-slate-300 px-4"
                      />
                      <p className="text-xs text-slate-500">{fieldHelp.hero_subtitle}</p>
                    </div>
                    <div className="space-y-2 lg:col-span-2">
                      <ImageUploadField
                        label="Logo"
                        helper={fieldHelp.logo_url}
                        recommended="Recommended 512×512"
                        value={form.logo_url}
                        onChange={(url) =>
                          setForm((prev) => ({ ...prev, logo_url: url }))
                        }
                        onUpload={handlePortalLogoUpload}
                        onRemove={handlePortalLogoRemove}
                        maxSizeMb={2}
                        urlLabel="Or paste a logo URL"
                      />
                    </div>
                    <div className="space-y-2 lg:col-span-2">
                      <ImageUploadField
                        label="Background"
                        helper={fieldHelp.background_url}
                        recommended="Recommended 1600×900"
                        value={form.background_url}
                        onChange={(url) =>
                          setForm((prev) => ({ ...prev, background_url: url }))
                        }
                        onUpload={handlePortalBackgroundUpload}
                        onRemove={handlePortalBackgroundRemove}
                        maxSizeMb={5}
                        urlLabel="Or paste a background URL"
                      />
                    </div>
                    <ColorControl
                      label="Primary color"
                      value={form.primary_color}
                      onChange={(value) =>
                        setForm((prev) => ({ ...prev, primary_color: value }))
                      }
                      helper={fieldHelp.primary_color}
                      fallbackColor={DEFAULT_PORTAL_PRIMARY_COLOR}
                      resetValue={DEFAULT_PORTAL_PRIMARY_COLOR}
                    />
                    <ColorControl
                      label="Header / nav bar color"
                      value={form.portal_nav_color}
                      onChange={(value) =>
                        setForm((prev) => ({ ...prev, portal_nav_color: value }))
                      }
                      helper={fieldHelp.portal_nav_color}
                      fallbackColor={brandPreviewColors.primary}
                      resetValue={DEFAULT_PORTAL_PRIMARY_COLOR}
                    />
                    <ColorControl
                      label="Button text color"
                      value={form.portal_button_text_color}
                      onChange={(value) =>
                        setForm((prev) => ({
                          ...prev,
                          portal_button_text_color: value,
                        }))
                      }
                      helper={fieldHelp.portal_button_text_color}
                      fallbackColor={brandPreviewColors.primaryText}
                      resetValue={DEFAULT_PORTAL_TEXT_COLOR}
                      warning={buttonContrastWarning}
                    />
                    <ColorControl
                      label="Header text color"
                      value={form.portal_nav_text_color}
                      onChange={(value) =>
                        setForm((prev) => ({ ...prev, portal_nav_text_color: value }))
                      }
                      helper={fieldHelp.portal_nav_text_color}
                      fallbackColor={brandPreviewColors.navText}
                      resetValue={DEFAULT_PORTAL_TEXT_COLOR}
                      warning={navContrastWarning}
                    />
                    <div className="space-y-3 rounded-2xl border border-slate-200 bg-slate-50 p-4 lg:col-span-2">
                      <div className="text-sm font-semibold text-slate-900">
                        Brand preview
                      </div>
                      <div
                        className="flex flex-wrap items-center justify-between gap-3 rounded-xl px-4 py-3"
                        style={{
                          backgroundColor: brandPreviewColors.nav,
                          color: brandPreviewColors.navText,
                        }}
                      >
                        <div className="font-semibold">
                          {form.brand_name || salonName || "Your Salon"}
                        </div>
                        <div className="flex flex-wrap gap-2">
                          <span
                            className="rounded-full px-3 py-1 text-xs font-semibold"
                            style={{
                              backgroundColor: "#ffffff",
                              color: brandPreviewColors.navActiveText,
                            }}
                          >
                            Book
                          </span>
                          <span
                            className="rounded-full px-3 py-1 text-xs font-semibold"
                            style={{
                              backgroundColor: "rgba(255,255,255,0.12)",
                              color: brandPreviewColors.navText,
                            }}
                          >
                            Profile
                          </span>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <span
                          className="rounded-full px-4 py-2 text-xs font-semibold"
                          style={{
                            backgroundColor: brandPreviewColors.primary,
                            color: brandPreviewColors.primaryText,
                          }}
                        >
                          Start Booking
                        </span>
                        <span
                          className="rounded-full border px-4 py-2 text-xs font-semibold"
                          style={{
                            borderColor: brandPreviewColors.primary,
                            color: brandPreviewColors.primary,
                          }}
                        >
                          Secondary action
                        </span>
                      </div>
                    </div>
                  <div className="space-y-2">
                    <label className="text-sm md:text-base font-semibold tracking-wide text-slate-500">
                      Client cancel/modify cutoff (hours)
                      </label>
                      <input
                        type="number"
                        min={0}
                        max={168}
                        step={1}
                        value={form.client_cancel_modify_cutoff_hours}
                        onChange={(e) =>
                          setForm((prev) => ({
                            ...prev,
                            client_cancel_modify_cutoff_hours: e.target.value,
                          }))
                        }
                        className="h-11 md:h-12 w-full rounded-xl border border-slate-300 px-4"
                      />
                      <p className="text-xs text-slate-500">
                        {fieldHelp.client_cancel_modify_cutoff_hours}
                      </p>
                      {cutoffError && (
                        <div className="text-xs text-rose-600">{cutoffError}</div>
                      )}
                    </div>
                  </div>

                </div>

                <div className="rounded-2xl border border-slate-200 bg-white p-6 space-y-6">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <h3 className="text-lg md:text-xl font-semibold text-slate-900">
                        Client Emails
                      </h3>
                      <p className="text-sm text-slate-500">
                        Customize the emails your clients receive for registration and password resets.
                      </p>
                      <p className="mt-1 text-xs text-slate-500">
                        Tip: keep the message short, friendly, and include the link token.
                      </p>
                    </div>
                    {renderSaveSettingsButton("shrink-0")}
                  </div>

                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5 space-y-4">
                    <div>
                      <h4 className="text-base md:text-lg font-semibold text-slate-900">
                        Registration Email
                      </h4>
                      <p className="text-xs text-slate-500">
                        Sent after a client registers for the portal.
                      </p>
                    </div>
                    <label className="flex items-center gap-3 text-sm md:text-base text-slate-700">
                      <input
                        type="checkbox"
                        checked={!!form.registration_email_template_enabled}
                        onChange={(e) =>
                          setForm((prev) => ({
                            ...prev,
                            registration_email_template_enabled: e.target.checked,
                          }))
                        }
                        className="h-4 w-4 rounded border-slate-300 text-slate-900"
                      />
                      <span className="font-semibold text-slate-700">
                        {renderEmailLabelWithTooltip(
                          "Use custom message",
                          "use_custom_message"
                        )}
                      </span>
                    </label>
                    <p className="text-xs text-slate-500">
                      {portalEmailFieldHelp.registration_toggle}
                    </p>

                    <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-6">
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <label className="text-sm md:text-base font-semibold tracking-wide text-slate-500">
                            {renderEmailLabelWithTooltip("Subject", "subject")}
                          </label>
                          <input
                            ref={registrationSubjectRef}
                            type="text"
                            value={form.registration_email_subject}
                            onChange={(e) =>
                              setForm((prev) => ({
                                ...prev,
                                registration_email_subject: e.target.value,
                              }))
                            }
                            onFocus={() => setActiveEmailField("registration_subject")}
                            disabled={!form.registration_email_template_enabled}
                            className="h-11 md:h-12 w-full rounded-xl border border-slate-300 px-4 disabled:bg-slate-100"
                          />
                          <p className="text-xs text-slate-500">
                            {portalEmailFieldHelp.registration_subject}
                          </p>
                        </div>
                        <div className="space-y-2">
                          <label className="text-sm md:text-base font-semibold tracking-wide text-slate-500">
                            {renderEmailLabelWithTooltip("Message body", "body")}
                          </label>
                          <textarea
                            ref={registrationBodyRef}
                            rows={7}
                            value={form.registration_email_body}
                            onChange={(e) =>
                              setForm((prev) => ({
                                ...prev,
                                registration_email_body: e.target.value,
                              }))
                            }
                            onFocus={() => setActiveEmailField("registration_body")}
                            disabled={!form.registration_email_template_enabled}
                            className="w-full rounded-2xl border border-slate-300 px-4 py-3 text-sm md:text-base disabled:bg-slate-100"
                          />
                          <p className="text-xs text-slate-500">
                            {portalEmailFieldHelp.registration_body}
                          </p>
                        </div>

                        <div className="space-y-2">
                          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 inline-flex items-center gap-2">
                            {renderEmailLabelWithTooltip("Tokens", "tokens")}
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {REGISTRATION_TOKENS.map((tokenValue) => (
                              <button
                                key={tokenValue}
                                type="button"
                                onClick={() =>
                                  handleEmailTokenInsert(tokenValue, "registration")
                                }
                                title="Click to copy"
                                className="rounded-full border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-600 hover:bg-slate-50"
                              >
                                {tokenValue}
                              </button>
                            ))}
                          </div>
                        </div>

                        <div className="flex flex-wrap items-center gap-2">
                          <button
                            type="button"
                            onClick={() =>
                              setForm((prev) => ({
                                ...prev,
                                registration_email_subject:
                                  portalEmailDefaults.registration.subject,
                                registration_email_body:
                                  portalEmailDefaults.registration.body,
                              }))
                            }
                            className="rounded-full border border-slate-300 px-4 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                          >
                            Reset to default
                          </button>
                        </div>
                      </div>

                      <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 space-y-3">
                        <div className="flex items-center justify-between gap-2">
                          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 inline-flex items-center gap-2">
                            {renderEmailLabelWithTooltip("Email preview", "preview")}
                          </div>
                          <div className="text-xs text-slate-500">
                            Preview uses sample values.
                          </div>
                        </div>
                        <div className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs text-slate-600">
                          <div>
                            <span className="font-semibold text-slate-700">From:</span>{" "}
                            {(salonName || "Your Salon") + " via Ellyanna <no-reply@ellyanna.com>"}
                          </div>
                          <div>
                            <span className="font-semibold text-slate-700">To:</span>{" "}
                            client@example.com
                          </div>
                          <div>
                            <span className="font-semibold text-slate-700">Subject:</span>{" "}
                            {registrationPreviewSubject || "No subject"}
                          </div>
                        </div>
                        <SafeHtml
                          className="rounded-xl border border-slate-200 bg-white px-3 py-3 text-sm text-slate-700 whitespace-pre-wrap"
                           html={registrationPreviewBodyHtml} />
                      </div>
                    </div>
                  </div>

                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5 space-y-4">
                    <div>
                      <h4 className="text-base md:text-lg font-semibold text-slate-900">
                        Password Reset Email
                      </h4>
                      <p className="text-xs text-slate-500">
                        Sent when a client requests a password reset.
                      </p>
                    </div>
                    <label className="flex items-center gap-3 text-sm md:text-base text-slate-700">
                      <input
                        type="checkbox"
                        checked={!!form.password_reset_email_template_enabled}
                        onChange={(e) =>
                          setForm((prev) => ({
                            ...prev,
                            password_reset_email_template_enabled: e.target.checked,
                          }))
                        }
                        className="h-4 w-4 rounded border-slate-300 text-slate-900"
                      />
                      <span className="font-semibold text-slate-700">
                        {renderEmailLabelWithTooltip(
                          "Use custom message",
                          "use_custom_message"
                        )}
                      </span>
                    </label>
                    <p className="text-xs text-slate-500">
                      {portalEmailFieldHelp.password_toggle}
                    </p>

                    <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-6">
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <label className="text-sm md:text-base font-semibold tracking-wide text-slate-500">
                            {renderEmailLabelWithTooltip("Subject", "subject")}
                          </label>
                          <input
                            ref={resetSubjectRef}
                            type="text"
                            value={form.password_reset_email_subject}
                            onChange={(e) =>
                              setForm((prev) => ({
                                ...prev,
                                password_reset_email_subject: e.target.value,
                              }))
                            }
                            onFocus={() => setActiveEmailField("reset_subject")}
                            disabled={!form.password_reset_email_template_enabled}
                            className="h-11 md:h-12 w-full rounded-xl border border-slate-300 px-4 disabled:bg-slate-100"
                          />
                          <p className="text-xs text-slate-500">
                            {portalEmailFieldHelp.password_subject}
                          </p>
                        </div>
                        <div className="space-y-2">
                          <label className="text-sm md:text-base font-semibold tracking-wide text-slate-500">
                            {renderEmailLabelWithTooltip("Message body", "body")}
                          </label>
                          <textarea
                            ref={resetBodyRef}
                            rows={7}
                            value={form.password_reset_email_body}
                            onChange={(e) =>
                              setForm((prev) => ({
                                ...prev,
                                password_reset_email_body: e.target.value,
                              }))
                            }
                            onFocus={() => setActiveEmailField("reset_body")}
                            disabled={!form.password_reset_email_template_enabled}
                            className="w-full rounded-2xl border border-slate-300 px-4 py-3 text-sm md:text-base disabled:bg-slate-100"
                          />
                          <p className="text-xs text-slate-500">
                            {portalEmailFieldHelp.password_body}
                          </p>
                        </div>

                        <div className="space-y-2">
                          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 inline-flex items-center gap-2">
                            {renderEmailLabelWithTooltip("Tokens", "tokens")}
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {PASSWORD_RESET_TOKENS.map((tokenValue) => (
                              <button
                                key={tokenValue}
                                type="button"
                                onClick={() =>
                                  handleEmailTokenInsert(tokenValue, "reset")
                                }
                                title="Click to copy"
                                className="rounded-full border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-600 hover:bg-slate-50"
                              >
                                {tokenValue}
                              </button>
                            ))}
                          </div>
                        </div>

                        <div className="flex flex-wrap items-center gap-2">
                          <button
                            type="button"
                            onClick={() =>
                              setForm((prev) => ({
                                ...prev,
                                password_reset_email_subject:
                                  portalEmailDefaults.password_reset.subject,
                                password_reset_email_body:
                                  portalEmailDefaults.password_reset.body,
                              }))
                            }
                            className="rounded-full border border-slate-300 px-4 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                          >
                            Reset to default
                          </button>
                        </div>
                      </div>

                      <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 space-y-3">
                        <div className="flex items-center justify-between gap-2">
                          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 inline-flex items-center gap-2">
                            {renderEmailLabelWithTooltip("Email preview", "preview")}
                          </div>
                          <div className="text-xs text-slate-500">
                            Preview uses sample values.
                          </div>
                        </div>
                        <div className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs text-slate-600">
                          <div>
                            <span className="font-semibold text-slate-700">From:</span>{" "}
                            {(salonName || "Your Salon") + " via Ellyanna <no-reply@ellyanna.com>"}
                          </div>
                          <div>
                            <span className="font-semibold text-slate-700">To:</span>{" "}
                            client@example.com
                          </div>
                          <div>
                            <span className="font-semibold text-slate-700">Subject:</span>{" "}
                            {passwordPreviewSubject || "No subject"}
                          </div>
                        </div>
                        <SafeHtml
                          className="rounded-xl border border-slate-200 bg-white px-3 py-3 text-sm text-slate-700 whitespace-pre-wrap"
                           html={passwordPreviewBodyHtml} />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="rounded-2xl border border-slate-200 bg-white p-6 space-y-5">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <h3 className="text-lg md:text-xl font-semibold text-slate-900">
                        Portal Contact & Location
                      </h3>
                      <p className="text-sm text-slate-500">
                        This information will show on your online booking portal.
                      </p>
                      <p className="mt-1 text-xs text-slate-500">
                        These details appear on your online booking portal landing page.
                      </p>
                    </div>
                    {renderSaveSettingsButton("shrink-0")}
                  </div>

                  <div className="space-y-2">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <label className="text-sm md:text-base font-semibold tracking-wide text-slate-500">
                        Portal contact phone
                      </label>
                      <button
                        type="button"
                        onClick={resetPortalPhone}
                        disabled={!portalContactOverrides?.phone}
                        className="text-xs font-semibold text-slate-600 underline disabled:opacity-60"
                      >
                        Reset to default
                      </button>
                    </div>
                    <input
                      type="text"
                      value={portalEffectivePhone}
                      onChange={(e) =>
                        handlePortalContactChange(
                          "phone",
                          e.target.value,
                          portalDefaultPhone
                        )
                      }
                      className="h-11 md:h-12 w-full rounded-xl border border-slate-300 px-4"
                    />
                    <p className="text-xs text-slate-500">
                      Shown on the portal. Leave blank to use your salon setup phone number.
                    </p>
                    {!portalContactOverrides?.phone && portalDefaultPhone && (
                      <div className="text-xs text-slate-500">
                        Using default: {formatNANP(portalDefaultPhone)}
                      </div>
                    )}
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="text-sm md:text-base font-semibold text-slate-800">
                      Address
                    </div>
                    <button
                      type="button"
                      onClick={resetPortalAddress}
                      disabled={!hasAddressOverride}
                      className="text-xs font-semibold text-slate-600 underline disabled:opacity-60"
                    >
                      Reset address to default
                    </button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2 md:col-span-2">
                      <label className="text-sm md:text-base font-semibold tracking-wide text-slate-500">
                        Address line 1
                      </label>
                      <input
                        type="text"
                        value={portalEffectiveAddress.line1}
                        onChange={(e) =>
                          handlePortalContactChange(
                            "address_line1",
                            e.target.value,
                            portalDefaultAddress?.line1
                          )
                        }
                        className="h-11 md:h-12 w-full rounded-xl border border-slate-300 px-4"
                      />
                    </div>
                    <div className="space-y-2 md:col-span-2">
                      <label className="text-sm md:text-base font-semibold tracking-wide text-slate-500">
                        Address line 2 (optional)
                      </label>
                      <input
                        type="text"
                        value={portalEffectiveAddress.line2}
                        onChange={(e) =>
                          handlePortalContactChange(
                            "address_line2",
                            e.target.value,
                            portalDefaultAddress?.line2
                          )
                        }
                        className="h-11 md:h-12 w-full rounded-xl border border-slate-300 px-4"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm md:text-base font-semibold tracking-wide text-slate-500">
                        City
                      </label>
                      <input
                        type="text"
                        value={portalEffectiveAddress.city}
                        onChange={(e) =>
                          handlePortalContactChange(
                            "city",
                            e.target.value,
                            portalDefaultAddress?.city
                          )
                        }
                        className="h-11 md:h-12 w-full rounded-xl border border-slate-300 px-4"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm md:text-base font-semibold tracking-wide text-slate-500">
                        Province / State
                      </label>
                      <input
                        type="text"
                        value={portalEffectiveAddress.province}
                        onChange={(e) =>
                          handlePortalContactChange(
                            "province",
                            e.target.value,
                            portalDefaultAddress?.province
                          )
                        }
                        className="h-11 md:h-12 w-full rounded-xl border border-slate-300 px-4"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm md:text-base font-semibold tracking-wide text-slate-500">
                        Postal Code
                      </label>
                      <input
                        type="text"
                        value={portalEffectiveAddress.postal_code}
                        onChange={(e) =>
                          handlePortalContactChange(
                            "postal_code",
                            e.target.value,
                            portalDefaultAddress?.postal_code
                          )
                        }
                        className="h-11 md:h-12 w-full rounded-xl border border-slate-300 px-4"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm md:text-base font-semibold tracking-wide text-slate-500">
                        Country
                      </label>
                      <input
                        type="text"
                        value={portalEffectiveAddress.country}
                        onChange={(e) =>
                          handlePortalContactChange(
                            "country",
                            e.target.value,
                            portalDefaultAddress?.country
                          )
                        }
                        className="h-11 md:h-12 w-full rounded-xl border border-slate-300 px-4"
                      />
                    </div>
                  </div>
                  <p className="text-xs text-slate-500">
                    Leave blank to use your salon setup address.
                  </p>

                    {mapEmbedUrl && (
                      <div className="space-y-2">
                        <div className="text-sm font-semibold text-slate-700">
                          Map preview
                        </div>
                      <div className="rounded-2xl border border-slate-200 overflow-hidden">
                        <iframe
                          title="Portal address map preview"
                          src={mapEmbedUrl}
                          className="h-64 w-full"
                          loading="lazy"
                          referrerPolicy="no-referrer-when-downgrade"
                        />
                      </div>
                      {mapLinkUrl && (
                        <a
                          href={mapLinkUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="text-xs font-semibold text-slate-600 underline"
                        >
                          Open map in a new tab
                        </a>
                        )}
                      </div>
                    )}

                    <div className="space-y-3">
                      <div>
                        <div className="text-sm md:text-base font-semibold text-slate-800">
                          Social links (optional)
                        </div>
                        <p className="text-xs text-slate-500">
                          Add your social links so they can be reused in campaign shares.
                        </p>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <label className="text-sm font-semibold text-slate-600">
                            Facebook URL
                          </label>
                          <input
                            type="url"
                            value={form.facebook_url}
                            onChange={(e) =>
                              setForm((prev) => ({ ...prev, facebook_url: e.target.value }))
                            }
                            placeholder="https://www.facebook.com/your-salon"
                            className="h-11 md:h-12 w-full rounded-xl border border-slate-300 px-4"
                          />
                        </div>
                        <div className="space-y-2">
                          <label className="text-sm font-semibold text-slate-600">
                            Instagram URL
                          </label>
                          <input
                            type="url"
                            value={form.instagram_url}
                            onChange={(e) =>
                              setForm((prev) => ({ ...prev, instagram_url: e.target.value }))
                            }
                            placeholder="https://www.instagram.com/your-salon"
                            className="h-11 md:h-12 w-full rounded-xl border border-slate-300 px-4"
                          />
                        </div>
                        <div className="space-y-2">
                          <label className="text-sm font-semibold text-slate-600">
                            TikTok URL
                          </label>
                          <input
                            type="url"
                            value={form.tiktok_url}
                            onChange={(e) =>
                              setForm((prev) => ({ ...prev, tiktok_url: e.target.value }))
                            }
                            placeholder="https://www.tiktok.com/@your-salon"
                            className="h-11 md:h-12 w-full rounded-xl border border-slate-300 px-4"
                          />
                        </div>
                        <div className="space-y-2">
                          <label className="text-sm font-semibold text-slate-600">
                            YouTube URL
                          </label>
                          <input
                            type="url"
                            value={form.youtube_url}
                            onChange={(e) =>
                              setForm((prev) => ({ ...prev, youtube_url: e.target.value }))
                            }
                            placeholder="https://www.youtube.com/@your-salon"
                            className="h-11 md:h-12 w-full rounded-xl border border-slate-300 px-4"
                          />
                        </div>
                      </div>
                      <p className="text-xs text-slate-500">
                        Use full URLs so links open correctly.
                      </p>
                    </div>

                    <div className="space-y-3">
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div>
                          <div className="text-sm md:text-base font-semibold text-slate-800">
                          Portal hours
                        </div>
                        <p className="text-xs text-slate-500">
                          Leave blank to use your salon setup hours.
                        </p>
                        {!portalHoursIsOverride && (
                          <div className="text-xs text-slate-500">
                            Using salon hours defaults.
                          </div>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={handleCopyPortalHours}
                          className="rounded-full border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                        >
                          Copy from salon hours
                        </button>
                        <button
                          type="button"
                          onClick={handleResetPortalHours}
                          disabled={!portalHoursIsOverride}
                          className="rounded-full border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60"
                        >
                          Reset to default
                        </button>
                      </div>
                    </div>

                    <div className="space-y-2">
                      {portalHoursDraft.map((row) => (
                        <div
                          key={row.key}
                          className="flex flex-col gap-2 rounded-xl border border-slate-200 bg-white p-3 md:flex-row md:items-center md:gap-4"
                        >
                          <div className="w-20 text-sm font-semibold text-slate-700">
                            {row.label}
                          </div>
                          <label className="flex items-center gap-2 text-xs text-slate-600">
                            <input
                              type="checkbox"
                              checked={row.is_closed}
                              onChange={(e) =>
                                updatePortalHourField(
                                  row.day_of_week,
                                  "is_closed",
                                  e.target.checked
                                )
                              }
                              className="h-4 w-4 rounded border-slate-300 text-slate-900"
                            />
                            Closed
                          </label>
                          {!row.is_closed && (
                            <div className="flex flex-1 flex-wrap items-center gap-2">
                              <TimeSelect
                                value={row.open_time || ""}
                                onChange={(val) =>
                                  updatePortalHourField(
                                    row.day_of_week,
                                    "open_time",
                                    val
                                  )
                                }
                                className="flex-1"
                              />
                              <span className="text-xs text-slate-500">to</span>
                              <TimeSelect
                                value={row.close_time || ""}
                                onChange={(val) =>
                                  updatePortalHourField(
                                    row.day_of_week,
                                    "close_time",
                                    val
                                  )
                                }
                                className="flex-1"
                              />
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                  </>
                )}

                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 space-y-3">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <h2 className="text-sm md:text-base font-semibold text-slate-800">
                        What this page does
                      </h2>
                      <p className="text-xs text-slate-500">
                        Turn the booking helper on/off and configure how it recommends
                        services in the client portal.
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setGuideOpen((prev) => !prev)}
                      className="rounded-full border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                    >
                      {guideOpen ? "Hide" : "Show"}
                    </button>
                  </div>
                  {guideOpen && (
                    <div className="text-sm text-slate-600 space-y-3">
                      <div>
                        <div className="font-semibold text-slate-700">
                          What you should do here
                        </div>
                        <ol className="mt-2 list-decimal space-y-1 pl-5">
                          <li>Enable the booking helper (optional)</li>
                          <li>Choose assistant tone</li>
                          <li>
                            Auto-map service categories and review only unclear services so
                            suggestions are accurate
                          </li>
                        </ol>
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  {renderSaveSettingsButton("h-11 md:h-12 px-6 text-base md:text-lg")}
                </div>

                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 space-y-4">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <h3 className="text-sm md:text-base font-semibold text-slate-800">
                        Client Booking Helper
                      </h3>
                      <p className="text-xs text-slate-500">
                        Let clients describe what they want and get service recommendations.
                      </p>
                    </div>
                    {renderSaveSettingsButton("shrink-0")}
                  </div>
                  <label className="flex items-center gap-3 text-sm md:text-base text-slate-700">
                    <input
                      type="checkbox"
                      checked={!!form.portal_ai_enabled}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          portal_ai_enabled: e.target.checked,
                        }))
                      }
                      className="h-4 w-4 rounded border-slate-300 text-slate-900"
                    />
                    <span className="font-semibold text-slate-700">
                      Enable Client Booking Helper
                    </span>
                  </label>
                  <p className="text-xs text-slate-500">
                    {fieldHelp.portal_ai_enabled}
                  </p>

                  <label className="flex items-center gap-3 text-sm md:text-base text-slate-700">
                    <input
                      type="checkbox"
                      checked={!!form.portal_ai_autoselect_enabled}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          portal_ai_autoselect_enabled: e.target.checked,
                        }))
                      }
                      className="h-4 w-4 rounded border-slate-300 text-slate-900"
                      disabled={!form.portal_ai_enabled}
                    />
                    <span className="font-semibold text-slate-700">
                      Allow auto-select
                    </span>
                  </label>
                  <p className="text-xs text-slate-500">
                    {fieldHelp.portal_ai_autoselect_enabled}
                  </p>

                  <div className="space-y-2">
                    <label className="text-sm md:text-base font-semibold tracking-wide text-slate-500">
                      Assistant tone
                    </label>
                    <select
                      value={form.portal_ai_tone}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          portal_ai_tone: e.target.value,
                        }))
                      }
                      disabled={!form.portal_ai_enabled}
                      className="h-11 md:h-12 w-full rounded-xl border border-slate-300 bg-white px-4"
                    >
                      <option value="friendly">Friendly</option>
                      <option value="professional">Professional</option>
                    </select>
                    <p className="text-xs text-slate-500">
                      {fieldHelp.portal_ai_tone}
                    </p>
                  </div>
                </div>

                    <div className="space-y-2">
                      <label className="text-sm md:text-base font-semibold tracking-wide text-slate-500">
                        Default pricing behavior
                      </label>
                      <select
                        value={form.default_ai_pricing_response_mode}
                        onChange={(e) =>
                          setForm((prev) => ({
                            ...prev,
                            default_ai_pricing_response_mode: e.target.value,
                          }))
                        }
                        disabled={!form.portal_ai_enabled}
                        className="h-11 md:h-12 w-full rounded-xl border border-slate-300 bg-white px-4"
                      >
                        <option value="EXACT_PRICE">Show exact prices</option>
                        <option value="STARTING_AT_PRICE">Show starting prices</option>
                        <option value="HIDDEN_PRICE">Do not show prices</option>
                      </select>
                    <p className="text-xs text-slate-500">
                      {fieldHelp.default_ai_pricing_response_mode}
                    </p>
                  </div>

                    <button
                      type="button"
                      onClick={() =>
                        setShowAdvancedAssistantSettings((prev) => !prev)
                      }
                      className="w-fit rounded-full border border-slate-300 px-4 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                    >
                      {showAdvancedAssistantSettings
                        ? "Hide advanced settings"
                        : "Show advanced settings"}
                    </button>

                    {showAdvancedAssistantSettings && (
                      <div className="rounded-2xl border border-slate-200 bg-white p-4 space-y-4">
                    <label className="flex items-center gap-3 text-sm md:text-base text-slate-700">
                      <input
                        type="checkbox"
                        checked={!!form.enable_ai_price_estimates}
                        onChange={(e) =>
                          setForm((prev) => ({
                            ...prev,
                            enable_ai_price_estimates: e.target.checked,
                          }))
                        }
                        className="h-4 w-4 rounded border-slate-300 text-slate-900"
                        disabled={!form.portal_ai_enabled}
                      />
                      <span className="font-semibold text-slate-700">
                        Allow helper to share prices
                      </span>
                    </label>
                    <p className="text-xs text-slate-500">
                      {fieldHelp.enable_ai_price_estimates}
                    </p>

                    <label className="flex items-center gap-3 text-sm md:text-base text-slate-700">
                      <input
                        type="checkbox"
                        checked={!!form.allow_service_level_pricing_override}
                        onChange={(e) =>
                          setForm((prev) => ({
                            ...prev,
                            allow_service_level_pricing_override: e.target.checked,
                          }))
                        }
                        className="h-4 w-4 rounded border-slate-300 text-slate-900"
                        disabled={!form.portal_ai_enabled}
                      />
                      <span className="font-semibold text-slate-700">
                        Allow per-service pricing choices
                      </span>
                    </label>
                      <p className="text-xs text-slate-500">
                        {fieldHelp.allow_service_level_pricing_override}
                      </p>

                    {form.default_ai_pricing_response_mode ===
                      "STARTING_AT_PRICE" && (
                    <div className="space-y-2">
                      <label className="text-sm md:text-base font-semibold tracking-wide text-slate-500">
                        Default starting-at disclaimer
                      </label>
                      <textarea
                        value={form.default_ai_pricing_disclaimer}
                        onChange={(e) =>
                          setForm((prev) => ({
                            ...prev,
                            default_ai_pricing_disclaimer: e.target.value,
                          }))
                        }
                        disabled={!form.portal_ai_enabled}
                        rows={3}
                        className="min-h-[96px] w-full rounded-xl border border-slate-300 bg-white px-4 py-3"
                        placeholder="Final pricing may vary based on length, design, and service details."
                      />
                      <p className="text-xs text-slate-500">
                        {fieldHelp.default_ai_pricing_disclaimer}
                      </p>
                    </div>
                    )}

                    {form.default_ai_pricing_response_mode ===
                      "HIDDEN_PRICE" && (
                    <div className="space-y-2">
                      <label className="text-sm md:text-base font-semibold tracking-wide text-slate-500">
                        Hidden-price assistant message
                      </label>
                      <textarea
                        value={form.hidden_price_assistant_message}
                        onChange={(e) =>
                          setForm((prev) => ({
                            ...prev,
                            hidden_price_assistant_message: e.target.value,
                          }))
                        }
                        disabled={!form.portal_ai_enabled}
                        rows={3}
                        className="min-h-[96px] w-full rounded-xl border border-slate-300 bg-white px-4 py-3"
                        placeholder="Pricing depends on the service details. I can help you book for an exact quote."
                      />
                      <p className="text-xs text-slate-500">
                        {fieldHelp.hidden_price_assistant_message}
                      </p>
                    </div>
                    )}
                      </div>
                    )}

                {form.portal_ai_enabled && (
                  <div
                    ref={helperSetupRef}
                    className="rounded-2xl border border-slate-200 bg-white p-6 space-y-4"
                  >
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div>
                        <h3 className="text-sm md:text-base font-semibold text-slate-800">
                          Service Matching
                        </h3>
                        <p className="text-xs text-slate-500">
                          Services are auto-mapped first. Review only unclear items so the helper recommends the
                          right booking.
                        </p>
                      </div>
                      <span
                        className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${
                          helperConfirmed
                            ? "bg-emerald-100 text-emerald-700"
                            : "bg-amber-100 text-amber-700"
                        }`}
                      >
                        {helperConfirmed
                          ? "Ready"
                          : helperReadyToConfirm
                            ? "Ready to save"
                            : "Needs review"}
                      </span>
                    </div>

                    <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                      <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                        <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                          Categories mapped
                        </div>
                        <div className="mt-2 text-2xl font-semibold text-slate-900">
                          {helperMappedCategoriesCount} of {helperCategoriesTotal}
                        </div>
                        <p className="mt-1 text-xs text-slate-500">
                          Most salons only need to review categories when a new service group is added.
                        </p>
                      </div>
                      <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                        <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                          Services reviewed
                        </div>
                        <div className="mt-2 text-2xl font-semibold text-slate-900">
                          {helperReviewedServicesCount} of {helperServicesTotal}
                        </div>
                        <p className="mt-1 text-xs text-slate-500">
                          Category matches count as reviewed unless a service clearly conflicts.
                        </p>
                      </div>
                      <div
                        className={`rounded-2xl border p-4 ${
                          helperNeedsReviewCount > 0
                            ? "border-amber-200 bg-amber-50"
                            : "border-emerald-200 bg-emerald-50"
                        }`}
                      >
                        <div
                          className={`text-xs font-semibold uppercase tracking-wide ${
                            helperNeedsReviewCount > 0
                              ? "text-amber-700"
                              : "text-emerald-700"
                          }`}
                        >
                          Needs review
                        </div>
                        <div className="mt-2 text-2xl font-semibold text-slate-900">
                          {helperNeedsReviewCount}
                        </div>
                        <p className="mt-1 text-xs text-slate-600">
                          {helperNeedsReviewCount > 0
                            ? "Only these services need a quick decision."
                            : "No service matching cleanup needed right now."}
                        </p>
                      </div>
                    </div>

                    <div className="rounded-2xl border border-slate-200 bg-white p-4">
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <div className="text-sm font-semibold text-slate-800">
                          How service matching works
                        </div>
                        <button
                          type="button"
                          onClick={() => setHelperGuideOpen((prev) => !prev)}
                          className="rounded-full border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                        >
                          {helperGuideOpen ? "Hide" : "Show"}
                        </button>
                      </div>
                      {!helperGuideOpen && (
                        <p className="mt-2 text-xs text-slate-500">
                          Quick guide to prevent wrong service bookings.
                        </p>
                      )}
                      {helperGuideOpen && (
                        <div className="mt-3 space-y-4 text-sm text-slate-600">
                          <div>
                            <div className="font-semibold text-slate-700">
                              Why this matters
                            </div>
                            <ul className="mt-2 list-disc space-y-1 pl-5">
                              <li>
                                This setup helps clients book the correct service.
                              </li>
                              <li>
                                Example: If a client wants longer nails, they should
                                book Artificial Nails (extensions), not a Manicure.
                              </li>
                            </ul>
                          </div>

                          <div>
                            <div className="font-semibold text-slate-700">
                              Step-by-step setup
                            </div>
                            <ul className="mt-2 list-disc space-y-1 pl-5">
                              <li>Step 1: Click Auto-map to get suggestions.</li>
                              <li>
                                Step 2: Review each Category and choose what it really
                                is (Natural nails, Artificial nails, Feet, Add-ons,
                                Waxing, Lashes, Facials, PMU).
                              </li>
                              <li>
                                Step 3: Check Services needing review and fix any
                                that are in the wrong group.
                              </li>
                              <li>Step 4: Click Save & confirm.</li>
                            </ul>
                          </div>

                          <div>
                            <div className="font-semibold text-slate-700">
                              Tips (common examples)
                            </div>
                            <ul className="mt-2 list-disc space-y-1 pl-5">
                              <li>Manicure = natural nails only (no length).</li>
                              <li>
                                Full Set / Extensions / Acrylic / Gel-X = artificial
                                nails.
                              </li>
                              <li>Refill/Fill = artificial nails maintenance.</li>
                              <li>Pedicure = feet.</li>
                              <li>
                                Gel polish and nail art can be for natural OR
                                artificial nails.
                              </li>
                            </ul>
                          </div>

                          <div>
                            <div className="font-semibold text-slate-700">
                              What clients will see
                            </div>
                            <ul className="mt-2 list-disc space-y-1 pl-5">
                              <li>
                                Clients will get better suggestions and fewer
                                booking mistakes.
                              </li>
                              <li>
                                The portal helper will guide them between natural
                                nails vs artificial nails vs feet vs add-ons.
                              </li>
                            </ul>
                          </div>

                          <div>
                            <div className="font-semibold text-slate-700">
                              When to update this
                            </div>
                            <ul className="mt-2 list-disc space-y-1 pl-5">
                              <li>
                                Update service matching when you add new categories or
                                services.
                              </li>
                              <li>
                                If a new service shows in "needs review", just
                                assign it and save.
                              </li>
                            </ul>
                          </div>
                        </div>
                      )}
                    </div>

                    {helperError && (
                      <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-xs text-rose-700">
                        {helperError}
                      </div>
                    )}
                    {helperSuccess && (
                      <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-xs text-emerald-700">
                        {helperSuccess}
                      </div>
                    )}

                    {helperLoading ? (
                      <div className="text-sm text-slate-500">
                        Loading helper setup...
                      </div>
                    ) : helperMapping ? (
                      <>
                        <button
                          type="button"
                          onClick={() =>
                            setShowCategoryMappingSettings((prev) => !prev)
                          }
                          className="w-fit rounded-full border border-slate-300 px-4 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                        >
                          {showCategoryMappingSettings
                            ? "Hide category settings"
                            : "Show category settings"}
                        </button>

                        {showCategoryMappingSettings && (
                        <div className="rounded-2xl border border-slate-200 overflow-hidden">
                          <div className="grid grid-cols-1 md:grid-cols-4 gap-2 bg-slate-50 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                            <div>Category</div>
                            <div>Services</div>
                            <div className="md:col-span-2">Lane</div>
                          </div>
                          {helperCategories.length === 0 ? (
                            <div className="px-4 py-4 text-sm text-slate-500">
                              No service categories found.
                            </div>
                          ) : (
                            helperCategories.map((category) => {
                              const count = serviceCounts[String(category.id)] || 0;
                              const selectedLane =
                                categoryLaneMapDraft?.[String(category.id)] ||
                                "UNKNOWN";
                              return (
                                <div
                                  key={category.id}
                                  className="grid grid-cols-1 md:grid-cols-4 gap-2 px-4 py-3 border-t border-slate-200 text-sm text-slate-700"
                                >
                                  <div className="font-semibold text-slate-800">
                                    {category.name}
                                  </div>
                                  <div className="text-xs text-slate-500">
                                    {count} services
                                  </div>
                                  <div className="md:col-span-2">
                                    <select
                                      value={selectedLane}
                                      onChange={(e) =>
                                        handleCategoryLaneChange(
                                          category.id,
                                          e.target.value
                                        )
                                      }
                                      className="h-10 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm"
                                    >
                                      {LANE_GROUPS.map((group) => (
                                        <optgroup
                                          key={group.label}
                                          label={group.label}
                                        >
                                          {group.options.map((option) => (
                                            <option
                                              key={option.value}
                                              value={option.value}
                                            >
                                              {option.label}
                                            </option>
                                          ))}
                                        </optgroup>
                                      ))}
                                    </select>
                                  </div>
                                </div>
                              );
                            })
                          )}
                        </div>

                        )}

                        <div className="space-y-1">
                          <div className="flex flex-wrap items-center gap-2">
                            {showAutoMap && (
                              <button
                                type="button"
                                onClick={handleAutoMap}
                                disabled={helperAutoMapping}
                                className="rounded-full border border-slate-300 px-4 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60"
                              >
                                {helperAutoMapping ? "Auto-mapping..." : "Auto-map services"}
                              </button>
                            )}
                            {showRemap && (
                              <button
                                type="button"
                                onClick={openRemapModal}
                                disabled={helperRemapping}
                                className="rounded-full border border-slate-300 px-4 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60"
                              >
                                {helperRemapping ? "Auto-mapping..." : "Auto-map again"}
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => handleSaveHelper(false)}
                              disabled={helperSaving}
                              className="rounded-full border border-slate-300 px-4 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60"
                            >
                              {helperSaving ? "Saving..." : "Save"}
                            </button>
                            <button
                              type="button"
                              onClick={() => handleSaveHelper(true)}
                              disabled={helperSaving}
                              className="rounded-full bg-slate-900 px-4 py-2 text-xs font-semibold text-white hover:bg-slate-800 disabled:opacity-60"
                            >
                              {helperSaving ? "Saving..." : "Save and mark ready"}
                            </button>
                          </div>
                          {helperLastRemapAt && (
                            <div className="text-xs text-slate-500">
                              Last auto-map: {formatRemapTimestamp(helperLastRemapAt)}
                            </div>
                          )}
                        </div>

                        {helperExpanded && hasReviewItems && (
                          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 space-y-3">
                            <div className="flex items-center justify-between">
                              <div>
                                <div className="text-sm font-semibold text-slate-800">
                                  {reviewMode === "ALL"
                                    ? "Review all services"
                                    : "Services needing attention"}
                                </div>
                                <div className="text-xs text-slate-500">
                                  {reviewMode === "ALL"
                                    ? "Review the services below and save when finished."
                                    : "Only unclear or conflicting services are shown here."}
                                </div>
                              </div>
                              <div className="text-xs font-semibold text-amber-700">
                                {reviewMode === "ALL"
                                  ? servicesNeedingReview.length
                                  : helperReviewItems.length}{" "}
                                items
                              </div>
                            </div>

                            <div className="flex flex-wrap gap-2">
                              <button
                                type="button"
                                onClick={applySuggestedMappingsToReviewItems}
                                className="rounded-full border border-slate-300 bg-white px-4 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                              >
                                Apply suggested matches
                              </button>
                              <button
                                type="button"
                                onClick={() => setShowCategoryMappingSettings(true)}
                                className="rounded-full border border-slate-300 bg-white px-4 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                              >
                                Review category settings
                              </button>
                            </div>


                            {reviewMode === "ALL" && (
                              <div className="flex flex-wrap gap-2">
                                <input
                                  type="text"
                                  value={allReviewSearch}
                                  onChange={(e) =>
                                    setAllReviewSearch(e.target.value)
                                  }
                                  placeholder="Search service name..."
                                  className="h-9 flex-1 min-w-[200px] rounded-xl border border-slate-300 bg-white px-3 text-xs"
                                />
                                <select
                                  value={allReviewLane}
                                  onChange={(e) =>
                                    setAllReviewLane(e.target.value)
                                  }
                                  className="h-9 min-w-[180px] rounded-xl border border-slate-300 bg-white px-3 text-xs"
                                >
                                  <option value="">All suggested lanes</option>
                                  {LANE_GROUPS.map((group) => (
                                    <optgroup key={group.label} label={group.label}>
                                      {group.options.map((option) => (
                                        <option
                                          key={option.value}
                                          value={option.value}
                                        >
                                          {option.label}
                                        </option>
                                      ))}
                                    </optgroup>
                                  ))}
                                </select>
                                <select
                                  value={allReviewCategory}
                                  onChange={(e) =>
                                    setAllReviewCategory(e.target.value)
                                  }
                                  className="h-9 min-w-[180px] rounded-xl border border-slate-300 bg-white px-3 text-xs"
                                >
                                  <option value="">All categories</option>
                                  {reviewCategoryOptions.map((option) => (
                                    <option
                                      key={option.value}
                                      value={option.value}
                                    >
                                      {option.label}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            )}

                            {reviewMode === "ALL" &&
                              servicesNeedingReview.length > 0 &&
                              helperReviewItems.length === 0 && (
                                <div className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs text-slate-500">
                                  No services match the current filters.
                                </div>
                              )}

                            {visibleHelperReviewItems.map((item) => {
                              const overrideValue =
                                serviceLaneOverridesDraft?.[
                                  String(item.service_id)
                                ] || "";
                              return (
                                <div
                                  key={item.service_id}
                                  className="rounded-xl border border-slate-200 bg-white p-3 text-sm"
                                >
                                  <div className="flex flex-wrap items-center justify-between gap-2">
                                    <div>
                                      <div className="font-semibold text-slate-800">
                                        {item.service_name}
                                      </div>
                                      <div className="text-xs text-slate-500">
                                        {item.category_name || "Uncategorized"} ·{" "}
                                        {item.duration_minutes || 0} mins
                                      </div>
                                    </div>
                                    <div className="text-xs text-slate-500">
                                      Suggested:{" "}
                                      {formatSuggestedLane(item) ||
                                        item.suggested_lane}
                                    </div>
                                  </div>
                                  <div className="mt-2">
                                    <select
                                      value={overrideValue}
                                      onChange={(e) =>
                                        handleServiceOverrideChange(
                                          item.service_id,
                                          e.target.value === "__CLEAR__"
                                            ? ""
                                            : e.target.value
                                        )
                                      }
                                      className="h-10 w-full rounded-xl border border-slate-300 bg-white px-3 text-xs"
                                    >
                                      <option value="">Use category mapping</option>
                                      <option value="__CLEAR__">
                                        Clear override (use category mapping)
                                      </option>
                                      {LANE_GROUPS.map((group) => (
                                        <optgroup
                                          key={group.label}
                                          label={group.label}
                                        >
                                          {group.options.map((option) => (
                                            <option
                                              key={option.value}
                                              value={option.value}
                                            >
                                              {option.label}
                                            </option>
                                          ))}
                                        </optgroup>
                                      ))}
                                    </select>
                                  </div>
                                </div>
                              );
                            })}

                            {hiddenHelperReviewCount > 0 && (
                              <div className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs text-slate-500">
                                Showing first 20. Use search filters or save the current matches, then continue reviewing.
                              </div>
                            )}
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="text-sm text-slate-500">
                        Helper setup data is not available yet.
                      </div>
                    )}
                  </div>
                )}
                <div className="flex items-center justify-end gap-2">
                  {renderSaveSettingsButton("h-11 md:h-12 px-6 text-base md:text-lg")}
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
      <SBINModal
        isOpen={helperRemapOpen}
        onClose={() => setHelperRemapOpen(false)}
        title="Auto-map Service Matching?"
        size="sm"
        fitContent={true}
        closeOnBackdrop={true}
        closeOnEscape={true}
        footer={
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setHelperRemapOpen(false)}
              className="rounded-full border border-slate-300 px-5 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleRemap}
              disabled={helperRemapping}
              className="rounded-full bg-slate-900 px-5 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-60"
            >
              {helperRemapping ? (
                <span className="inline-flex items-center gap-2">
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  Auto-mapping...
                </span>
              ) : (
                "Auto-map"
              )}
            </button>
          </div>
        }
      >
        <div className="space-y-4 text-sm text-slate-700">
          <p>
            This will refresh suggestions based on your current categories and
            services.
          </p>
          <p>
            Your current saved mapping will NOT be changed until you click Save.
          </p>
          <div className="space-y-3">
            <label className="flex items-start gap-3 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
              <input
                type="radio"
                name="helper-remap-mode"
                value="replace"
                checked={helperRemapMode === "replace"}
                onChange={() => setHelperRemapMode("replace")}
                className="mt-1 h-4 w-4 text-slate-900"
              />
              <div>
                <div className="font-semibold text-slate-800">
                  Auto-map everything
                </div>
                <div className="text-xs text-slate-500">
                  This refreshes all matches and lets you review every service.
                </div>
              </div>
            </label>
            <label className="flex items-start gap-3 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
              <input
                type="radio"
                name="helper-remap-mode"
                value="categories"
                checked={helperRemapMode === "categories"}
                onChange={() => setHelperRemapMode("categories")}
                className="mt-1 h-4 w-4 text-slate-900"
              />
              <div>
                <div className="font-semibold text-slate-800">
                  Auto-map categories only
                </div>
                <div className="text-xs text-slate-500">
                  Update category mapping suggestions, but keep my service overrides.
                </div>
              </div>
            </label>
            <label className="flex items-start gap-3 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
              <input
                type="radio"
                name="helper-remap-mode"
                value="fill"
                checked={helperRemapMode === "fill"}
                onChange={() => setHelperRemapMode("fill")}
                className="mt-1 h-4 w-4 text-slate-900"
              />
              <div>
                <div className="font-semibold text-slate-800">
                  Only fill empty items
                </div>
                <div className="text-xs text-slate-500">
                  Only fill items that are still Unknown or not set.
                </div>
              </div>
            </label>
          </div>
        </div>
      </SBINModal>
      {helperToastMessage && (
        <div
          className={`fixed bottom-6 right-6 z-[10060] rounded-2xl border px-4 py-3 text-sm font-semibold shadow-lg ${
            helperToastVariant === "error"
              ? "border-rose-200 bg-rose-50 text-rose-700"
              : helperToastVariant === "info"
              ? "border-slate-200 bg-slate-50 text-slate-700"
              : "border-emerald-200 bg-emerald-50 text-emerald-700"
          }`}
        >
          {helperToastMessage}
        </div>
      )}
    </div>
  );
}
